/**
 * GameplayScreen
 * 
 * Main gameplay container that renders the current scene with all interactive elements
 */

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useGame, AnimationController, SceneExecutor, useAssetPrefetch, diffInventoryUnlockNotices, loadInventoryCampaignStore } from '../systems';
import type { InventoryUnlockNotice } from '../systems/inventory-manager';
import { getScenarioAssetCacheKey, resolveScenarioAssetUrl } from '../systems/scenario-asset-url';
import {
  findChapterEndNavigationTarget,
  resolveChapterEnd,
  type ChapterEndPresentation,
} from '../systems/chapter-end-content';
import { resolveTextOverlayOptions, resolveTextOverlayType } from '../systems/text-overlay-content';
import { findPrecedingDialogue, resolveNextSceneId, resolveVisualState } from '../systems/scene-flow';
import { Scenario, Scene, SceneElement, ClickAction, TextOverlayScene, CommandItem, ConfidenceGainScene, PhoneChatScene, PhoneMailScene, PhoneMailInteractScene, PhoneMailPreview, PhoneMailTriageAction, PhoneMailHighlightItem, PhoneMailTriageReactionStep, PasswordGameScene, DragDropGameScene, SituationSortGameScene, TimedOverlayScene, ChoiceScene, ObservationScene, ClickableScreenScene, ClickableScreenZone, GameOverScene, RecordCardScene, formatUnknownSceneTypeMessage, isKnownSceneType } from '../types';
import { resolveTriageReactionSteps, triageStepHasHighlight } from '../systems/phone-mail-triage';
import {
  CHOICE_CONFIDENCE_SCENE_ID,
  computeChapterConfidenceGains,
  resolveChoiceConfidenceGains,
} from '../systems/confidence-utils';
import { EMAIL_ZOOM_FLY_MS } from './phone-mail';
import styles from './gameplay-screen.module.css';
import { SceneRenderer, SceneRendererHandle } from './scene-renderer';
import { DialogueBox } from './dialogue-box';
import { ChoicePanel, isGenericChoicePrompt } from './choice-panel';
import { QuickFadePresence, QUICK_UI_FADE_MS } from './quick-fade-presence';
import { StopButton } from './stop-button';
import { TextOverlay } from './text-overlay';
import { ChapterEndScreen } from './chapter-end-screen';
import { ChapterProgressCard } from './chapter-progress-card';
import { GameOverScreen } from './game-over-screen';
import { RecordCardScreen, resolveRecordCardSerialDisplay } from './record-card-screen';
import { AttackCardScreen, mapAttackCardSceneToProps } from './attack-card-screen';
import { PersistentUI } from './persistent-ui';
import { InventoryOverlay } from './inventory-overlay';
import { DeveloperMenu } from './developer-menu';
import { ToastNotification, ToastData } from './toast-notification';
import { EndScreen } from './end-screen';
import { ErrorScreen } from './error-screen';
import { AchievementCelebration } from './achievement-celebration';
import { ConfidenceGainOverlay } from './confidence-gain-overlay';
import { ShieldGainOverlay } from './shield-gain-overlay';
import { WrongAnswerFlash } from './wrong-answer-flash';
import { ObjectInspectOverlay } from './object-inspect-overlay';
import { ObservationOverlay } from './observation-overlay';
import { ClickableScreenOverlay } from './clickable-screen-overlay';
import {
  clickableZoneToChoiceOption,
  getClickableScreenMaxPedaShields,
  getClickableScreenRewardCategory,
  getVisibleClickableZones,
} from '../systems/clickable-screen-utils';
import { Phone, PhoneState } from './phone';
import { PhoneChat } from './phone-chat';
import { PhoneCall } from './phone-call';
import { InlineCall } from './inline-call';
import { isInlinePhoneCallStyle } from '../systems/phone-call-style';
import { PhoneMail, PhoneMailTriageContext, MailPhase } from './phone-mail';
import { PasswordGame } from './password-game';
import { DragDropGame } from './drag-drop-game';
import { SituationSortGame } from './situation-sort-game';
import { PhoneGameplayImmersiveOverlays } from './phone-gameplay-immersive';
import { useCleanupPhoneImmersiveOnUnmount } from '../hooks/use-phone-gameplay-immersive';
import { SHARED_ASSETS } from '../shared-assets';
import { filterChaptersForMission, getLocalCompletedMissions, getMissionIdForScene, getMissionLabel, getMissionStartScene, resolveActiveMissionId } from '../systems/mission-progress';
import { buildCampaignFrise, findMissionStartingAt, resolveGeneratedMissionOpening } from '../systems/campaign-frise';
import { isVideoAsset } from '../systems/media-utils';
import { unlockBackgroundAudio } from '../systems/media-utils';
import { useBackgroundMusic } from '../hooks/use-background-music';
import { useAuth } from '../systems/auth-context';
import { canAccessGameDeveloperMenu, canSubmitReview } from '../systems/roles';
import { getPedaCategoryLabel, normalizePedaCategory, type PedaCategoryKey } from '../systems/peda-categories';
import {
  buildAllyTrustDisplayRows,
  flushLiveMissionTrustToCampaign,
  loadTrustCampaignStore,
  mergeCampaignTrustCounts,
  readLiveTrustCounts,
  trustVariableKey,
  type AllyTrustDisplayRow,
} from '../systems/ally-trust';
import {
  buildPedaEndStats,
  collectPedaShieldsByCategory,
  flushLiveMissionToCampaign,
  loadPedaCampaignStore,
  mergeCampaignEarnedCounts,
  pedaCategoryVariableKey,
  readChapterStartCategoryCounts,
  readLiveCategoryCounts,
  resolvePedaRank,
  sumPedaCategoryCounts,
  type PedaCategoryCounts,
} from '../systems/peda-shield-stats';
import { recordPedaEvent } from '../systems/peda-events';
import { isNewGameOver, reportGameOver, reportMissionResult } from '../systems/mission-results';
import { createReview } from '../systems/review-api';
import { getChapterLabelForScene, getSceneExcerptWithSpeaker } from '../systems/review-context';
import { ReviewFab } from './reviews/ReviewFab';
import { isMotionReduced } from '../systems/accessibility-settings';
import { useAccessibility, useStrictA11yDocument } from '../systems/accessibility-context';
import { ReviewCommentModal } from './reviews/ReviewCommentModal';
import {
  allAnomaliesInspected,
  getInspectedFlag,
  getObservationCompletionDialogues,
  getObservationGateFlag,
  getReportedFlag,
  isInspectObjectElement,
  isObjectInspectScene,
  resolveElementMessages,
  syncObjectInspectGate,
  syncObservationGate,
} from '../systems/object-inspect-utils';
import {
  resolveActiveCharacterElementId,
  resolveCharacterPortrait,
  resolveDialogueMood,
} from '../systems/character-runtime';

interface GameplayScreenProps {
  scenario: Scenario; // test
  scenarioId: string;
  missionId?: string;
  onBackToMenu: () => void;
  /** Called when the player advances to another mission in-game (e.g. chapter-end "Mission 2 →"). */
  onMissionChange?: (missionId: string) => void;
  /** Scenario Studio preview: isolated session, no URL/save restore, overlays portaled in-container. */
  previewMode?: boolean;
  /** Portal target for fullscreen overlays (defaults to document.body). */
  overlayPortalTarget?: HTMLElement | null;
}

const WRONG_ANSWER_FLASH_ENABLED = true;
const STOP_DEFAULT_MESSAGE = "Il n'y a pas besoin de stopper l'interaction ici";
/** Matches phone.module.css close transition */
const DIRECTOR_MAIL_CLOSE_MS = 600;

function findActiveStopOption(
  choice: ChoiceScene,
  evaluateCondition: (condition: string) => boolean
): number | null {
  for (let i = 0; i < choice.options.length; i++) {
    const opt = choice.options[i];
    if (opt.stopButton && (!opt.condition || evaluateCondition(opt.condition))) {
      return i;
    }
  }
  return null;
}

function sceneHasPhoneMailInteract(scene: Scene | null): boolean {
  return scene?.type === 'phoneMailInteract';
}

function getDirectorMailFields(
  scene: Scene | null
): (PhoneMailPreview & Partial<PhoneMailInteractScene>) | null {
  if (SceneExecutor.isPhoneMailInteract(scene)) {
    return scene;
  }
  if (scene?.type === 'choice' && scene.phoneMailPreview) {
    return scene.phoneMailPreview;
  }
  return null;
}

type ChapterEndBridgePhase = 'idle' | 'scene-exit' | 'black-hold' | 'summary-in';

const CHAPTER_END_SCENE_EXIT_MS = 720;
const CHAPTER_END_BLACK_HOLD_MS = 1200;
const CHAPTER_END_SUMMARY_DELAY_MS = CHAPTER_END_SCENE_EXIT_MS + CHAPTER_END_BLACK_HOLD_MS;

function shouldSkipSceneEntrance(scene: Scene | null): boolean {
  if (resolveChapterEnd(scene) !== null) return true;
  return SceneExecutor.isTextOverlay(scene);
}

function isObjectSearchScene(scene: Scene | null): boolean {
  if (!scene) return false;
  if (isObjectInspectScene(scene) || scene.id.startsWith('chapter3_')) {
    return true;
  }
  return Boolean(scene.elements?.some((element) => element.type === 'object' && element.clickAction));
}

export function GameplayScreen({
  scenario,
  scenarioId,
  missionId,
  onBackToMenu,
  onMissionChange,
  previewMode = false,
  overlayPortalTarget = null,
}: GameplayScreenProps): JSX.Element {
  const { strictMode: a11yStrict } = useAccessibility();
  useStrictA11yDocument(a11yStrict);
  useCleanupPhoneImmersiveOnUnmount(true);
  const { enabled: musicEnabled, toggle: toggleMusic, resume: resumeMusic } = useBackgroundMusic();
  const unlockGameAudio = useCallback(() => {
    unlockBackgroundAudio();
    resumeMusic();
  }, [resumeMusic]);
  const game = useGame();
  const { user } = useAuth();
  useAssetPrefetch(scenario, game.currentScene?.id ?? game.gameState?.currentSceneId);
  const sceneRendererRef = useRef<SceneRendererHandle>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isInventoryOpen, setIsInventoryOpen] = useState(false);
  const [inventoryUnlockQueue, setInventoryUnlockQueue] = useState<InventoryUnlockNotice[]>([]);
  const [inventoryUnlockNoticeKey, setInventoryUnlockNoticeKey] = useState(0);
  const [inventoryHasUnread, setInventoryHasUnread] = useState(false);
  const [inventoryFullCard, setInventoryFullCard] = useState<RecordCardScene | null>(null);
  const [inventoryFullCardExiting, setInventoryFullCardExiting] = useState(false);
  const [isDeveloperMenuOpen, setIsDeveloperMenuOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | ToastData | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [chapterEndExiting, setChapterEndExiting] = useState(false);
  const [chapterEndBridgePhase, setChapterEndBridgePhase] = useState<ChapterEndBridgePhase>('idle');
  const chapterEndBridgeTimersRef = useRef<number[]>([]);
  const chapterEndBridgeCaptionRef = useRef<string | null>(null);
  const [isEnded, setIsEnded] = useState(false);
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [endingMessage, setEndingMessage] = useState('');
  const [isTextOverlayOpen, setIsTextOverlayOpen] = useState(false);
  const [currentTextOverlay, setCurrentTextOverlay] = useState<TextOverlayScene | null>(null);
  const [activeChapterEnd, setActiveChapterEnd] = useState<ChapterEndPresentation | null>(null);
  /** Carte « repère de progression » vue : le bilan de fin de chapitre peut s'afficher. */
  const [chapterProgressAcknowledged, setChapterProgressAcknowledged] = useState(false);
  /** Ouverture de mission générée (mission sans lettre d'ouverture), par id de scène. */
  const [generatedOpeningSceneId, setGeneratedOpeningSceneId] = useState<string | null>(null);
  const generatedOpeningSeenRef = useRef<string | null>(null);
  const [activeGameOver, setActiveGameOver] = useState<GameOverScene | null>(null);
  const [activeRecordCard, setActiveRecordCard] = useState<RecordCardScene | null>(null);
  const [isConfidenceOverlayOpen, setIsConfidenceOverlayOpen] = useState(false);
  const [currentConfidenceGain, setCurrentConfidenceGain] = useState<ConfidenceGainScene | null>(null);
  const [confidenceStartLevels, setConfidenceStartLevels] = useState<Record<string, number>>({});
  const postConfidenceActionRef = useRef<(() => void) | null>(null);
  const [currentTimedOverlay, setCurrentTimedOverlay] = useState<TimedOverlayScene | null>(null);
  const [isMoodChanging, setIsMoodChanging] = useState(false);
  const [narrativeUiVisible, setNarrativeUiVisible] = useState(true);
  const narrativeUiFadeTimerRef = useRef<number | null>(null);
  const [mailTriageReactionSteps, setMailTriageReactionSteps] = useState<PhoneMailTriageReactionStep[] | null>(null);
  const [mailTriageStepIndex, setMailTriageStepIndex] = useState(0);
  const [mailTriageReactionIndex, setMailTriageReactionIndex] = useState(0);
  const [mailTriagePendingNav, setMailTriagePendingNav] = useState<string | null>(null);
  const [mailTriageHighlightInEmail, setMailTriageHighlightInEmail] = useState<PhoneMailHighlightItem[]>([]);
  const [mailTriageReactionsUnlocked, setMailTriageReactionsUnlocked] = useState(false);
  const [mailInboxReturnSignal, setMailInboxReturnSignal] = useState(0);
  const [mailTriageAllTriaged, setMailTriageAllTriaged] = useState(false);
  const [pendingShieldGain, setPendingShieldGain] = useState(0);
  const [pendingShieldSubtitle, setPendingShieldSubtitle] = useState<string | null>(null);
  const pendingShieldByCategoryRef = useRef<Partial<PedaCategoryCounts>>({});
  const [shieldAnimAmount, setShieldAnimAmount] = useState(0);
  const [shieldAnimSubtitle, setShieldAnimSubtitle] = useState('Vigilance messagerie');
  const [isShieldAnimOpen, setIsShieldAnimOpen] = useState(false);
  const [shieldHudPulse, setShieldHudPulse] = useState(false);
  const postShieldActionRef = useRef<(() => void) | null>(null);
  const shieldAnimCategoriesRef = useRef<Partial<PedaCategoryCounts>>({});
  const shieldHudRef = useRef<HTMLDivElement>(null);
  const shieldAppliedRef = useRef(false);
  const sceneContainerRef = useRef<HTMLDivElement>(null);
  const [wrongAnswerFlashKey, setWrongAnswerFlashKey] = useState(0);
  const [objectInspectGateTick, setObjectInspectGateTick] = useState(0);
  const [directorMailInboxReached, setDirectorMailInboxReached] = useState(false);
  const [directorMailReading, setDirectorMailReading] = useState(false);
  const [directorMailClosing, setDirectorMailClosing] = useState(false);
  const directorMailCloseTimerRef = useRef<number | null>(null);
  const prevInventoryRef = useRef<string[]>([]);
  const prevCatalogUnlockedRef = useRef<string[]>(
    loadInventoryCampaignStore(scenarioId).unlocked,
  );
  const prevHiddenElementsRef = useRef<Set<string>>(new Set());
  const isRestoringRef = useRef(false);
  const profileTargetRef = useRef<HTMLButtonElement>(null);
  const [achievementTargetPos, setAchievementTargetPos] = useState({ x: 0, y: 0 });
  const animationControllerRef = useRef<AnimationController | null>(null);
  const [inspectTarget, setInspectTarget] = useState<SceneElement | null>(null);
  const [clickedClickableZoneId, setClickedClickableZoneId] = useState<string | null>(null);
  const [stopFeedbackText, setStopFeedbackText] = useState<string | null>(null);
  const [sortGameHudProgress, setSortGameHudProgress] = useState<string | null>(null);

  // Prefix character asset paths with scenario base URL (+ cache-bust via metadata.version)
  const prefixedCharacterData = useMemo(() => {
    const charData = scenario.metadata.characters || [];
    const cacheKey = getScenarioAssetCacheKey(scenario);
    const prefix = (path?: string) =>
      resolveScenarioAssetUrl(path, scenarioId, cacheKey);
    return charData.map((char) => ({
      ...char,
      icon: prefix(char.icon),
      defaultImage: prefix(char.defaultImage) ?? char.defaultImage,
      emotions: char.emotions
        ? Object.fromEntries(
            Object.entries(char.emotions).map(([key, path]) => [key, prefix(path)!])
          )
        : undefined,
    }));
  }, [scenario.metadata.characters, scenario.metadata.version, scenarioId]);

  // Add runtime confidence to characters (changes during gameplay)
  const charactersWithConfidence = useMemo(() => {
    const variables = game.gameState?.variables || {};
    return prefixedCharacterData.map(char => ({
      ...char,
      confidence: (variables[`${char.id}_confidence`] as number) || 0
    }));
  }, [prefixedCharacterData, game.gameState?.variables]);

  // Register back to menu callback for __MAIN_MENU__ navigation
  useEffect(() => {
    game.setOnBackToMenu(onBackToMenu);
    return () => {
      game.setOnBackToMenu(null);
    };
  }, [game, onBackToMenu]);

  useEffect(() => {
    setInspectTarget(null);
  }, [game.currentScene?.id]);

  // Initialize animation controller
  useEffect(() => {
    animationControllerRef.current = new AnimationController();
    return () => {
      animationControllerRef.current?.killAll();
    };
  }, []);

  // When loading a completed scenario (e.g. re-opening the story), show end screen only if we're at a real end.
  // If current content is a confidence gain with goto, we're mid-story (stale save) — don't show end screen and clear the flag.
  useEffect(() => {
    if (!game.gameState?.scenarioCompleted) return;
    const content = game.getCurrentContent();
    const isConfidenceWithNextScene = content && SceneExecutor.isConfidenceGain(content) && Boolean(content.goto);
    if (isConfidenceWithNextScene) {
      game.clearScenarioCompleted(); // Fix save so we don't show end screen again after navigating
      return;
    }
    setIsEnded(true);
    setEndingMessage(prev => prev || 'Félicitations ! Vous avez terminé cette aventure.');
  }, [game.gameState?.scenarioCompleted, game.currentScene?.id, game]);

  // Check if story has ended and reset isEnded when scene changes
  useEffect(() => {
    if (!game.gameState || !game.currentScene) return;
    
    // Don't reset isEnded when scenario is completed (we always show end screen)
    if (game.gameState.scenarioCompleted) return;

    // Reset isEnded if we are at the beginning of a scene
    if (isEnded) return;

    const currentContent = game.getCurrentContent();
    const hasMore = game.hasMoreContent();
    const isAtChoice = game.isAtChoice();

    if (
      !hasMore
      && !isAtChoice
      && currentContent?.type === 'dialogue'
      && !currentContent.goto
    ) {
      // Extract ending message from the last dialogue
      const message = currentContent.text || 'Merci d\'avoir joué !';
      setEndingMessage(message);
      setIsEnded(true);
      game.markScenarioCompleted();
    }
  }, [game.currentScene?.id, isEnded, game]);

  useEffect(() => {
    setStopFeedbackText(null);
  }, [game.currentScene?.id]);

  // Handle scene entrance animations and reset isSceneReady
  useEffect(() => {
    if (!game.currentScene) {
      setIsSceneReady(false);
      return;
    }

    if (shouldSkipSceneEntrance(game.currentScene)) {
      setIsSceneReady(true);
      return;
    }

    const runEntrance = async () => {
      // Always reset isSceneReady when scene path changes (even if it's the same scene ID)
      setIsSceneReady(false);
      
      // Wait a bit for the scene to render and refs to be established
      await new Promise(resolve => setTimeout(resolve, 150));
      
      if (sceneRendererRef.current) {
        await sceneRendererRef.current.playEntranceAnimations();
        
        // Brief pause (0.2s) after entrance before dialogue
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      setIsSceneReady(true);
    };

    runEntrance();
    // Include currentScenePath string as dependency to detect navigation to same scene ID
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.currentScene?.id, game.gameState?.currentSceneId]);

  useEffect(() => {
    setMailTriageReactionSteps(null);
    setMailTriageStepIndex(0);
    setMailTriageReactionIndex(0);
    setMailTriagePendingNav(null);
    setMailTriageHighlightInEmail([]);
    setMailTriageReactionsUnlocked(false);
    setMailTriageAllTriaged(false);
    setPendingShieldGain(0);
    setPendingShieldSubtitle(null);
    pendingShieldByCategoryRef.current = {};
    setIsShieldAnimOpen(false);
    setShieldAnimAmount(0);
    postShieldActionRef.current = null;
    setClickedClickableZoneId(null);
    if (sceneHasPhoneMailInteract(game.currentScene)) {
      setDirectorMailInboxReached(false);
      setDirectorMailReading(false);
      setDirectorMailClosing(false);
      if (directorMailCloseTimerRef.current !== null) {
        window.clearTimeout(directorMailCloseTimerRef.current);
        directorMailCloseTimerRef.current = null;
      }
    }
  }, [game.currentScene?.id, game.gameState?.currentSceneId]);

  useEffect(() => {
    if (!game.currentScene || !game.gameState) return;
    const target = SceneExecutor.resolvePhoneStateTarget(
      scenario,
      game.gameState.currentSceneId
    );
    const current = (game.gameState.variables?.phoneState as number) ?? 0;
    if (current !== target) {
      game.setVariable('phoneState', target);
    }
  }, [
    game,
    scenario,
    game.currentScene?.id,
    game.gameState?.currentSceneId,
  ]);

  useEffect(() => {
    const content = game.getCurrentContent();
    if (content?.type === 'command') {
      game.advanceDialogue();
    }
  }, [game, game.currentScene?.id, game.gameState?.currentSceneId]);

  // Handle mood changes from dialogue
  useEffect(() => {
    if (!isSceneReady || !game.currentScene || !game.gameState) return;
    
    const content = game.getCurrentContent();
    const currentHidden = game.gameState.hiddenElements || new Set();
    const prevHidden = prevHiddenElementsRef.current;
    
    // Detect if an element was just shown (was hidden, now visible)
    const newlyShownElements: string[] = [];
    prevHidden.forEach((elementId) => {
      if (!currentHidden.has(elementId)) {
        newlyShownElements.push(elementId);
      }
    });
    
    // Update ref for next comparison
    prevHiddenElementsRef.current = new Set(currentHidden);
    
    // Apply character emotion from metadata.characters
    if (content?.type === 'dialogue' && !content.isInternal) {
      const elementId = resolveActiveCharacterElementId(content);
      const mood = resolveDialogueMood(content);
      if (elementId && mood) {
        const elementJustShown = newlyShownElements.includes(elementId);

        if (elementJustShown) {
          sceneRendererRef.current?.changeMood(elementId, mood, true);
        } else {
          setIsMoodChanging(true);
          sceneRendererRef.current?.changeMood(elementId, mood, false).then(() => {
            setIsMoodChanging(false);
          }).catch(() => {
            setIsMoodChanging(false);
          });
        }
        return;
      }
    }
    
    setIsMoodChanging(false);
  }, [game.currentScene?.id, game.gameState?.hiddenElements, isSceneReady, game]);

  // Handle confidence gain overlays appearing in dialogue flow
  useEffect(() => {
    if (isEnded) return; // Don't reopen overlay when story has ended (user chose end screen from confidence continue)
    const content = game.getCurrentContent();
    if (SceneExecutor.isConfidenceGain(content) && !isConfidenceOverlayOpen && !currentConfidenceGain) {
      setCurrentConfidenceGain(content);
      setIsConfidenceOverlayOpen(true);
    }
  }, [game.currentScene?.id, game, isConfidenceOverlayOpen, currentConfidenceGain, isEnded]);

  const activeMissionId = useMemo(
    () => resolveActiveMissionId({
      missionId,
      gameStateMissionId: game.gameState?.missionId,
      missions: scenario.metadata.missions,
      chapters: scenario.metadata.chapters,
      scenes: scenario.scenes,
      currentSceneId: game.gameState?.currentSceneId,
    }),
    [
      missionId,
      game.gameState?.missionId,
      game.gameState?.currentSceneId,
      scenario.metadata.missions,
      scenario.metadata.chapters,
      scenario.scenes,
    ]
  );

  // Repère de progression : frise de campagne à l'ouverture de mission et en fin de chapitre.
  const friseSceneId = game.gameState?.currentSceneId ?? '';
  const completedMissionIds = useMemo(
    () => getLocalCompletedMissions(scenarioId),
    // Relu à chaque scène : la mission précédente est marquée terminée en arrivant sur la suivante.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scenarioId, friseSceneId]
  );
  const missionOpeningFrise = useMemo(
    () => (friseSceneId && findMissionStartingAt(scenario, friseSceneId)
      ? buildCampaignFrise(scenario, { sceneId: friseSceneId, mode: 'opening', completedMissionIds })
      : null),
    [scenario, friseSceneId, completedMissionIds]
  );
  const chapterEndFrise = useMemo(
    () => (activeChapterEnd && friseSceneId
      ? buildCampaignFrise(scenario, { sceneId: friseSceneId, mode: 'chapterEnd', completedMissionIds })
      : null),
    [activeChapterEnd, scenario, friseSceneId, completedMissionIds]
  );
  const generatedOpening = useMemo(
    () => (friseSceneId ? resolveGeneratedMissionOpening(scenario, friseSceneId) : null),
    [scenario, friseSceneId]
  );

  useEffect(() => {
    if (!activeChapterEnd) setChapterProgressAcknowledged(false);
  }, [activeChapterEnd]);

  useLayoutEffect(() => {
    if (generatedOpening && missionOpeningFrise) {
      if (generatedOpeningSeenRef.current !== friseSceneId) {
        generatedOpeningSeenRef.current = friseSceneId;
        setGeneratedOpeningSceneId(friseSceneId);
      }
      return;
    }
    // Hors ouverture : l'ouverture réapparaîtra si la mission est rejouée.
    generatedOpeningSeenRef.current = null;
    setGeneratedOpeningSceneId(null);
  }, [friseSceneId, generatedOpening, missionOpeningFrise]);

  const isMissionOpeningOpen = Boolean(generatedOpeningSceneId) && generatedOpeningSceneId === friseSceneId;

  const closeGeneratedOpening = useCallback(() => {
    setGeneratedOpeningSceneId(null);
  }, []);

  const emitPedaEvent = useCallback((pedaCategory: PedaCategoryKey | null, correct: boolean) => {
    if (!pedaCategory) return;
    const sceneId = game.currentScene?.id ?? game.gameState?.currentSceneId ?? 'unknown';
    const missionIdForPeda = sceneId !== 'unknown'
      ? getMissionIdForScene(scenario.scenes, scenario.metadata.chapters, sceneId) ?? activeMissionId
      : activeMissionId;
    recordPedaEvent({
      scenarioId,
      missionId: missionIdForPeda,
      sceneId,
      pedaCategory,
      correct,
    });
  }, [scenarioId, activeMissionId, game.currentScene?.id, game.gameState?.currentSceneId, scenario.scenes, scenario.metadata.chapters]);

  const getChoiceSceneRewardCategory = useCallback((options: ChoiceScene['options']) => {
    const rewarding = options.find((o) => (o.pedaShields ?? 0) > 0);
    return normalizePedaCategory(rewarding?.pedaCategory);
  }, []);

  const missionChapters = useMemo(
    () => filterChaptersForMission(scenario.metadata.chapters, activeMissionId),
    [scenario.metadata.chapters, activeMissionId]
  );

  const missionLabel = useMemo(
    () => getMissionLabel(scenario.metadata.missions, activeMissionId),
    [scenario.metadata.missions, activeMissionId]
  );

  const developerScenario = useMemo(
    () => ({
      ...scenario,
      metadata: {
        ...scenario.metadata,
        chapters: missionChapters,
      },
    }),
    [scenario, missionChapters]
  );

  const missionStartScene = useMemo(() => {
    if (!activeMissionId) return undefined;
    return getMissionStartScene(scenario, activeMissionId);
  }, [activeMissionId, scenario]);

  const overlayPortalRoot = overlayPortalTarget ?? document.body;
  const overlayPortalsContained = overlayPortalTarget != null;

  // Initialize game on mount or when scenario/mission changes
  useEffect(() => {
    if (previewMode) {
      game.initializeGame(scenarioId, scenario, {
        force: true,
        startScene: scenario.metadata.startScene,
      });
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlMissionId = urlParams.get('mission') ?? undefined;
    const initMissionId = missionId ?? urlMissionId;

    if (
      game.gameState?.scenarioId === scenarioId
      && game.gameState?.missionId === initMissionId
      && game.currentScene
    ) {
      return;
    }

    const urlSceneId = urlParams.get('scene');

    // Try to load saved game, otherwise start new game at mission start.
    // Derive the start scene from the *launch* mission (prop/URL), never from the
    // live gameState mission — otherwise advancing to a later mission in-game would
    // retrigger this effect and reload the previous mission's save.
    const launchStartScene = initMissionId
      ? getMissionStartScene(scenario, initMissionId)
      : undefined;

    const loaded = initMissionId
      ? game.loadSavedGame(scenarioId, scenario, initMissionId)
      : game.loadSavedGame(scenarioId, scenario);

    if (!loaded) {
      game.initializeGame(scenarioId, scenario, {
        force: true,
        startScene: launchStartScene,
        missionId: initMissionId,
      });
    }
    
    // Restore position from URL if available (only if URL params exist)
    if (urlSceneId) {
      isRestoringRef.current = true;
      setTimeout(() => {
        if (game.gameState && urlSceneId !== game.gameState.currentSceneId) {
          game.navigateToScene(urlSceneId);
        }
        isRestoringRef.current = false;
      }, 100);
    } else {
      isRestoringRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, missionId, previewMode]);

  // Update URL when game state changes (but not during restoration)
  useEffect(() => {
    if (previewMode) return;
    if (!game.gameState || !game.currentScene) return;
    if (isRestoringRef.current) {
      // Reset flag after a short delay to ensure restoration is complete
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 200);
      return; // Don't update URL during restoration
    }
    
    const sceneId = game.gameState.currentSceneId;
    
    const urlParams = new URLSearchParams(window.location.search);
    const currentScene = urlParams.get('scene');
    
    if (currentScene !== sceneId) {
      urlParams.set('scenario', scenarioId);
      urlParams.set('scene', sceneId);
      urlParams.delete('step');
      
      const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [game.gameState?.currentSceneId, scenarioId, previewMode]);

  // When the player advances to another mission in-game, keep App + URL in sync so a
  // reload restores the correct mission save (saves are keyed by scenarioId + missionId).
  useEffect(() => {
    if (previewMode) return;
    const liveMissionId = game.gameState?.missionId;
    if (!liveMissionId || liveMissionId === missionId) return;

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('mission') !== liveMissionId) {
      urlParams.set('scenario', scenarioId);
      urlParams.set('mission', liveMissionId);
      window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
    }
    onMissionChange?.(liveMissionId);
  }, [game.gameState?.missionId, missionId, scenarioId, onMissionChange]);

  // Watch for save cleanup and show toast
  useEffect(() => {
    if (game.saveResult?.cleanedUpSaves && game.saveResult.cleanedUpSaves.length > 0) {
      setToastMessage(`Storage limit reached. Auto-cleaned ${game.saveResult.cleanedUpSaves.length} old save(s).`);
      game.clearSaveResult();
    }
  }, [game.saveResult, game.clearSaveResult]);

  // Watch for inventory changes and show toast
  useEffect(() => {
    if (!game.gameState) return;
    
    const currentInventory = game.gameState.inventory || [];
    const previousInventory = prevInventoryRef.current || [];
    
    // Only update if inventory actually changed
    if (currentInventory.length === previousInventory.length && 
        currentInventory.every((id, idx) => id === previousInventory[idx])) {
      return;
    }
    
    if (currentInventory.length > previousInventory.length) {
      // Find the new item
      const newItemId = currentInventory.find(id => !previousInventory.includes(id));
      if (newItemId) {
        const item = scenario.items.find(i => i.id === newItemId);
        if (item) {
          setToastMessage(`Objet collecté: ${item.name}`);
        }
      }
    }
    
    prevInventoryRef.current = [...currentInventory];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.gameState?.inventory]);

  useEffect(() => {
    prevCatalogUnlockedRef.current = [...loadInventoryCampaignStore(scenarioId).unlocked];
    setInventoryUnlockQueue([]);
    setInventoryHasUnread(false);
  }, [scenarioId]);

  useEffect(() => {
    const current = game.inventoryStore?.unlocked ?? [];
    // resetGame() wipes the in-memory store to [] before campaign ids are
    // reloaded. Do not treat that gap as "everything is new".
    if (current.length === 0 && prevCatalogUnlockedRef.current.length > 0) {
      return;
    }

    const notices = diffInventoryUnlockNotices(
      scenario.metadata.inventory,
      prevCatalogUnlockedRef.current,
      current,
      {
        scenes: scenario.scenes,
        characters: scenario.metadata.characters,
      },
    );
    prevCatalogUnlockedRef.current = [...current];
    if (notices.length === 0) return;
    setInventoryUnlockQueue((queue) => [...queue, ...notices]);
  }, [game.inventoryStore?.unlocked, scenario.metadata.inventory, scenario.scenes, scenario.metadata.characters]);

  const showGameDeveloperMenu = canAccessGameDeveloperMenu(user);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isTypingInField = activeElement?.tagName === 'INPUT'
        || activeElement?.tagName === 'TEXTAREA';

      // Ctrl+K — menu développeur (testeur et rôles supérieurs)
      if (
        showGameDeveloperMenu
        && event.key.toLowerCase() === 'k'
        && (event.ctrlKey || event.metaKey)
        && !event.altKey
        && !isEnded
      ) {
        event.preventDefault();
        setIsDeveloperMenuOpen((prev) => {
          const next = !prev;
          if (next) {
            setIsMenuOpen(false);
            setIsProfileOpen(false);
          }
          return next;
        });
        return;
      }

      if (isDeveloperMenuOpen) {
        return;
      }

      // ESC for menu / inventory
      if (event.key === 'Escape') {
        event.preventDefault();
        if (isInventoryOpen) {
          return;
        }
        if (isProfileOpen) {
          setIsProfileOpen(false);
        } else if (!isEnded) {
          setIsMenuOpen(prev => !prev);
        }
      }
      
      // 'P' key for profile (case insensitive)
      if (event.key.toLowerCase() === 'p' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (!isTypingInField) {
          event.preventDefault();
          if (!isEnded) {
            setIsProfileOpen(prev => !prev);
            if (isMenuOpen) {
              setIsMenuOpen(false);
            }
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMenuOpen, isProfileOpen, isInventoryOpen, isDeveloperMenuOpen, isEnded, showGameDeveloperMenu]);



  const clearChapterEndBridgeTimers = useCallback(() => {
    chapterEndBridgeTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    chapterEndBridgeTimersRef.current = [];
  }, []);

  const handleDeveloperNavigateToScene = useCallback((sceneId: string) => {
    setIsDeveloperMenuOpen(false);
    clearChapterEndBridgeTimers();
    setChapterEndBridgePhase('idle');
    setChapterEndExiting(false);
    setIsTextOverlayOpen(false);
    setActiveChapterEnd(null);
    setIsEnded(false);
    setIsTransitioning(true);
    game.navigateToScene(sceneId);
    window.setTimeout(() => setIsTransitioning(false), 300);
  }, [game, clearChapterEndBridgeTimers]);

  const scheduleChapterEndBridge = useCallback((fn: () => void, delayMs: number) => {
    const timerId = window.setTimeout(fn, delayMs);
    chapterEndBridgeTimersRef.current.push(timerId);
  }, []);

  const beginChapterEndBridge = useCallback((targetSceneId: string) => {
    clearChapterEndBridgeTimers();
    const targetScene = scenario.scenes.find((s) => s.id === targetSceneId);
    const chapterEndPresentation = targetScene ? resolveChapterEnd(targetScene) : null;
    chapterEndBridgeCaptionRef.current = chapterEndPresentation?.data.eyebrow ?? null;
    setChapterEndBridgePhase('scene-exit');

    scheduleChapterEndBridge(() => {
      setChapterEndBridgePhase('black-hold');
      game.navigateToScene(targetSceneId);
    }, CHAPTER_END_SCENE_EXIT_MS);

    scheduleChapterEndBridge(() => {
      setChapterEndBridgePhase('summary-in');
      chapterEndBridgeCaptionRef.current = null;
    }, CHAPTER_END_SUMMARY_DELAY_MS);
  }, [clearChapterEndBridgeTimers, scheduleChapterEndBridge, game, scenario.scenes]);

  useEffect(() => () => clearChapterEndBridgeTimers(), [clearChapterEndBridgeTimers]);

  const clearNarrativeUiFadeTimer = useCallback(() => {
    if (narrativeUiFadeTimerRef.current !== null) {
      window.clearTimeout(narrativeUiFadeTimerRef.current);
      narrativeUiFadeTimerRef.current = null;
    }
  }, []);

  const runNarrativeUiFade = useCallback((action: () => void) => {
    clearNarrativeUiFadeTimer();
    setNarrativeUiVisible(false);
    narrativeUiFadeTimerRef.current = window.setTimeout(() => {
      narrativeUiFadeTimerRef.current = null;
      action();
      setNarrativeUiVisible(true);
    }, QUICK_UI_FADE_MS);
  }, [clearNarrativeUiFadeTimer]);

  useEffect(() => {
    setNarrativeUiVisible(true);
    clearNarrativeUiFadeTimer();
  }, [game.currentScene?.id, clearNarrativeUiFadeTimer]);

  useEffect(() => () => clearNarrativeUiFadeTimer(), [clearNarrativeUiFadeTimer]);

  const handleDialogueClick = useCallback(() => {
    unlockGameAudio();
    if (game.gameState?.isAchievementAnimating) return;

    const content = game.getCurrentContent();
    const chapterEndTargetId = findChapterEndNavigationTarget(scenario, content);
    if (chapterEndTargetId) {
      beginChapterEndBridge(chapterEndTargetId);
      return;
    }

    if (content?.type === 'dialogue' && content.goto && !game.hasMoreContent()) {
      setIsTransitioning(true);
      setTimeout(() => {
        game.navigateToScene(content.goto!);
        setIsTransitioning(false);
      }, 300);
      return;
    }

    if (game.hasMoreContent() && !game.isAtChoice()) {
      runNarrativeUiFade(() => game.advanceDialogue());
    }
  }, [game, scenario, beginChapterEndBridge, runNarrativeUiFade]);

  const handleUnknownSceneContinue = useCallback(() => {
    unlockGameAudio();
    if (game.gameState?.isAchievementAnimating) return;
    if (game.hasMoreContent()) {
      runNarrativeUiFade(() => game.advanceDialogue());
    }
  }, [game, runNarrativeUiFade]);

  const beginShieldGainAnimation = useCallback((
    amount: number,
    afterComplete: () => void,
    subtitle = 'Compétence cyber',
    categoryGains?: PedaCategoryKey | Partial<PedaCategoryCounts> | null,
  ) => {
    if (typeof categoryGains === 'string') {
      shieldAnimCategoriesRef.current = { [categoryGains]: amount };
    } else if (categoryGains) {
      shieldAnimCategoriesRef.current = { ...categoryGains };
    } else {
      shieldAnimCategoriesRef.current = {};
    }
    shieldAppliedRef.current = false;
    postShieldActionRef.current = afterComplete;
    setShieldAnimSubtitle(subtitle);
    setShieldAnimAmount(amount);
    setIsShieldAnimOpen(true);
  }, []);

  const beginConfidenceGainAnimation = useCallback((
    gains: Array<{ characterId: string; confidenceLevel: number }>,
    startLevels: Record<string, number>,
    afterComplete: () => void,
    introText?: string,
    continueText?: string,
  ) => {
    setConfidenceStartLevels(startLevels);
    setCurrentConfidenceGain({
      id: CHOICE_CONFIDENCE_SCENE_ID,
      type: 'confidenceGain',
      characters: gains,
      introText,
      continueText,
    });
    postConfidenceActionRef.current = afterComplete;
    setIsConfidenceOverlayOpen(true);
  }, []);

  const handleChoice = useCallback(async (optionIndex: number) => {
    unlockGameAudio();
    if (game.gameState?.isAchievementAnimating) return;
    const content = game.getCurrentContent();
    if (content && content.type === 'choice') {
      const option = content.options[optionIndex];

      // Apply mood changes if any
      if (option.moodChanges && option.moodChanges.length > 0) {
        for (const change of option.moodChanges) {
          await sceneRendererRef.current?.changeMood(change.elementId, change.mood);
        }
        // Small pause to let the last mood change be visible if we transition
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      if ((content as ChoiceScene).phoneMailPreview) {
        setDirectorMailInboxReached(true);
      }

      const proceedChoice = () => {
        runNarrativeUiFade(() => game.makeChoice(option));
      };

      const validCharacterIds = new Set((scenario.metadata.characters ?? []).map((c) => c.id));
      const overlayCharacters: Array<{ characterId: string; confidenceLevel: number }> = [];
      const overlayStartLevels: Record<string, number> = {};

      if (option.trust?.points && option.trust.points !== 0 && validCharacterIds.has(option.trust.characterId)) {
        const trustKey = trustVariableKey(option.trust.characterId);
        const currentTrust = (game.gameState?.variables?.[trustKey] as number) ?? 0;
        overlayStartLevels[option.trust.characterId] = currentTrust;
        overlayCharacters.push({
          characterId: option.trust.characterId,
          confidenceLevel: currentTrust + option.trust.points,
        });
        game.setVariable(trustKey, currentTrust + option.trust.points);
      }

      const resolvedConfidenceGains = resolveChoiceConfidenceGains(
        option.confidenceGains,
        game.getConfidence,
        validCharacterIds,
      );
      for (const gain of resolvedConfidenceGains) {
        if (overlayCharacters.some((row) => row.characterId === gain.characterId)) continue;
        overlayCharacters.push({
          characterId: gain.characterId,
          confidenceLevel: gain.targetLevel,
        });
        overlayStartLevels[gain.characterId] = gain.startLevel;
      }

      const finishChoiceFlow = () => {
        if (overlayCharacters.length > 0 && !isConfidenceOverlayOpen) {
          beginConfidenceGainAnimation(
            overlayCharacters,
            overlayStartLevels,
            proceedChoice,
          );
          return;
        }
        proceedChoice();
      };

      const shieldsEarned = option.pedaShields ?? 0;
      const hasRewardingOption = content.options.some((o) => (o.pedaShields ?? 0) > 0);
      const sceneRewardCategory = getChoiceSceneRewardCategory(content.options);
      if (shieldsEarned > 0) {
        const optionCategory = normalizePedaCategory(option.pedaCategory) ?? sceneRewardCategory;
        if (optionCategory) emitPedaEvent(optionCategory, true);
      } else if (hasRewardingOption && sceneRewardCategory) {
        emitPedaEvent(sceneRewardCategory, false);
      }
      if (
        WRONG_ANSWER_FLASH_ENABLED
        && shieldsEarned === 0
        && hasRewardingOption
      ) {
        setWrongAnswerFlashKey((k) => k + 1);
      }

      if (shieldsEarned > 0 && !isShieldAnimOpen) {
        const optionCategory = normalizePedaCategory(option.pedaCategory) ?? sceneRewardCategory;
        beginShieldGainAnimation(
          shieldsEarned,
          finishChoiceFlow,
          optionCategory ? getPedaCategoryLabel(optionCategory) : 'Compétence cyber',
          optionCategory,
        );
        return;
      }

      finishChoiceFlow();
    }
  }, [
    game,
    scenario.metadata.characters,
    isShieldAnimOpen,
    isConfidenceOverlayOpen,
    beginShieldGainAnimation,
    beginConfidenceGainAnimation,
    unlockGameAudio,
    emitPedaEvent,
    getChoiceSceneRewardCategory,
    runNarrativeUiFade,
  ]);

  const handleStopClick = useCallback(() => {
    unlockGameAudio();
    if (
      isTransitioning
      || isShieldAnimOpen
      || isConfidenceOverlayOpen
      || game.gameState?.isAchievementAnimating
      || isMenuOpen
      || isProfileOpen
      || isDeveloperMenuOpen
      || isTextOverlayOpen
      || isMissionOpeningOpen
    ) {
      return;
    }

    const content = game.getCurrentContent();
    if (game.isAtChoice() && content?.type === 'choice') {
      const choice = content as ChoiceScene;
      const isObjSearch = isObjectSearchScene(game.currentScene);
      const visibleRegularOptions = choice.options.filter(
        (option) =>
          !option.stopButton
          && (!option.condition || game.evaluateCondition(option.condition))
      );
      const explorationInstructionActive = isObjSearch && visibleRegularOptions.length === 0;

      if (!explorationInstructionActive) {
        const stopIndex = findActiveStopOption(choice, game.evaluateCondition);
        if (stopIndex !== null) {
          setStopFeedbackText(null);
          handleChoice(stopIndex);
          return;
        }
      }
    }

    setStopFeedbackText(STOP_DEFAULT_MESSAGE);
  }, [
    game,
    isTransitioning,
    isShieldAnimOpen,
    isConfidenceOverlayOpen,
    isMenuOpen,
    isProfileOpen,
    isDeveloperMenuOpen,
    isTextOverlayOpen,
    isMissionOpeningOpen,
    handleChoice,
    unlockGameAudio,
  ]);

  const handleMailboxAction = useCallback((elementId: string, actionType: 'reply' | 'archive' | 'report' | 'clickOnLink') => {
    if (game.gameState?.isAchievementAnimating) return;
    const element = game.currentScene?.elements?.find(e => e.id === elementId);
    if (!element?.mailboxActions) return;

    const action = element.mailboxActions[actionType];
    if (!action) return;

    // Execute any actions associated with this mailbox action
    if (action.actions && action.actions.length > 0) {
      game.applyCommands(action.actions);
    }

    // Navigate to the target scene
    if (action.goto) {
      setIsTransitioning(true);
      setTimeout(() => {
        game.navigateToScene(action.goto!);
        setIsTransitioning(false);
      }, 300);
    }
  }, [game]);

  const processClickAction = useCallback((action: ClickAction, elementId?: string) => {
    if (action.condition && !game.evaluateCondition(action.condition)) {
      return;
    }

    switch (action.type) {
      case 'dialogue':
        if (typeof action.payload === 'string') {
          setToastMessage(action.payload);
        } else if (typeof action.payload === 'object' && action.payload !== null) {
          const toastPayload = action.payload as ToastData;
          setToastMessage({
            ...toastPayload,
            icon: toastPayload.icon
              ? resolveScenarioAssetUrl(
                  toastPayload.icon,
                  scenarioId,
                  getScenarioAssetCacheKey(scenario),
                )
              : undefined,
          });
        }
        break;
        
      case 'collectItem':
        if (typeof action.payload === 'string') {
          const itemId = action.payload;
          
          if (elementId && sceneRendererRef.current && profileTargetRef.current && animationControllerRef.current) {
            const element = sceneRendererRef.current.getElement(elementId);
            const profileBtn = profileTargetRef.current;
            
            if (element && profileBtn) {
              const elementRect = element.getBoundingClientRect();
              const profileRect = profileBtn.getBoundingClientRect();
              
              const targetX = profileRect.left + profileRect.width / 2 - (elementRect.left + elementRect.width / 2);
              const targetY = profileRect.top + profileRect.height / 2 - (elementRect.top + elementRect.height / 2);
              
              animationControllerRef.current.animateToPosition(element, targetX, targetY, {
                duration: 0.8,
                ease: 'power2.inOut',
                onComplete: () => {
                  element.style.display = 'none';
                  game.collectItem(itemId);
                  
                  // Execute nextAction if present
                  if (action.nextAction) {
                    processClickAction(action.nextAction, elementId);
                  }
                }
              });
              return;
            }
          }
          game.collectItem(itemId);
        }
        break;
        
      case 'setFlag':
        if (typeof action.payload === 'object' && action.payload !== null) {
          const { name, value } = action.payload as { name: string; value: boolean };
          game.setFlag(name, value);
        }
        break;
        
      case 'goto':
        if (typeof action.payload === 'string') {
          setIsTransitioning(true);
          setTimeout(() => {
            game.navigateToScene(action.payload as string);
            setIsTransitioning(false);
            
            // Execute nextAction if present (after transition completes)
            if (action.nextAction) {
              processClickAction(action.nextAction, elementId);
            }
          }, 300);
        }
        break;
        
      case 'changeBackground':
        if (typeof action.payload === 'string' && sceneRendererRef.current) {
          sceneRendererRef.current.changeBackground(action.payload);
          
          // Execute nextAction if present (immediately after background change)
          if (action.nextAction) {
            processClickAction(action.nextAction, elementId);
          }
        }
        break;
        
      case 'showTextOverlay':
        if (typeof action.payload === 'object' && action.payload !== null) {
          const overlayData = action.payload as TextOverlayScene;
          const chapterEnd = resolveChapterEnd(overlayData);
          if (chapterEnd) {
            setActiveChapterEnd(chapterEnd);
            setCurrentTextOverlay(null);
            setIsTextOverlayOpen(false);
          } else {
            setCurrentTextOverlay(overlayData);
            setIsTextOverlayOpen(true);
          }
          
          // Execute nextAction if present (after overlay is shown)
          if (action.nextAction) {
            processClickAction(action.nextAction, elementId);
          }
        }
        break;
    }
    
    // Execute nextAction if not already handled in specific cases
    // This catches cases where nextAction wasn't processed yet
    if (action.nextAction && 
        action.type !== 'collectItem' && 
        action.type !== 'goto' && 
        action.type !== 'changeBackground' && 
        action.type !== 'showTextOverlay') {
      processClickAction(action.nextAction, elementId);
    }
  }, [game]);

  const handleElementClick = useCallback((elementId: string) => {
    if (game.gameState?.isAchievementAnimating) return;
    if (inspectTarget) return;
    const content = game.getCurrentContent();
    if (content?.type === 'choice' && (content as ChoiceScene).phoneMailPreview) return;
    if (SceneExecutor.isPhoneMailInteract(content)) return;

    const scene = game.currentScene;
    const element = scene?.elements?.find(e => e.id === elementId);
    if (!element) return;

    // Mode inspection : modale zoom Signaler / Ne rien faire.
    if (element.type === 'object' && isInspectObjectElement(element)) {
      if (game.gameState?.flags?.[getInspectedFlag(elementId)]) {
        return;
      }
      setInspectTarget(element);
      return;
    }

    if (!element.clickAction) return;

    const action = element.clickAction;

    if (action.condition && !game.evaluateCondition(action.condition)) {
      return;
    }

    if (content?.type === 'choice') return;

    processClickAction(action, elementId);
  }, [game, inspectTarget, processClickAction]);

  const handleDirectorMailPhaseChange = useCallback((phase: MailPhase) => {
    if (phase === 'inbox') {
      setDirectorMailInboxReached(true);
      setDirectorMailReading(false);
    } else if (phase === 'reading') {
      setDirectorMailReading(true);
    }
    const content = game.getCurrentContent();
    if (
      SceneExecutor.isPhoneMailInteract(content)
      && content.startPhase === 'menu'
      && !content.waitForOpen
      && phase === 'inbox'
    ) {
      game.advanceDialogue();
    }
  }, [game]);

  const closeDirectorMailThen = useCallback((then: () => void) => {
    if (directorMailCloseTimerRef.current !== null) {
      window.clearTimeout(directorMailCloseTimerRef.current);
    }
    setDirectorMailReading(false);
    setDirectorMailInboxReached(false);
    setDirectorMailClosing(true);
    game.setVariable('phoneState', 0);
    setIsTransitioning(true);
    directorMailCloseTimerRef.current = window.setTimeout(() => {
      directorMailCloseTimerRef.current = null;
      setDirectorMailClosing(false);
      setIsTransitioning(false);
      then();
    }, DIRECTOR_MAIL_CLOSE_MS);
  }, [game]);

  useEffect(() => () => {
    if (directorMailCloseTimerRef.current !== null) {
      window.clearTimeout(directorMailCloseTimerRef.current);
    }
  }, []);

  const handleDirectorMailOpen = useCallback(() => {
    const content = game.getCurrentContent();
    if (!SceneExecutor.isPhoneMailInteract(content)) return;
    if (content.content) {
      setDirectorMailReading(true);
      return;
    }
    if (content.goto) {
      closeDirectorMailThen(() => {
        game.navigateToScene(content.goto!);
      });
      return;
    }
    game.advanceDialogue();
  }, [game, closeDirectorMailThen]);

  const handleDirectorMailReadingComplete = useCallback(() => {
    const content = game.getCurrentContent();
    if (!SceneExecutor.isPhoneMailInteract(content)) return;
    closeDirectorMailThen(() => {
      if (content.goto) {
        game.navigateToScene(content.goto);
      } else {
        game.advanceDialogue();
      }
    });
  }, [game, closeDirectorMailThen]);

  const handleMenuToggle = useCallback(() => {
    const content = game.getCurrentContent();
    if (content?.type === 'choice' && (content as ChoiceScene).phoneMailPreview) return;
    if (SceneExecutor.isPhoneMailInteract(content)) return;
    setIsMenuOpen(prev => !prev);
  }, [game]);

  const handleProfileToggle = useCallback(() => {
    const content = game.getCurrentContent();
    if (content?.type === 'choice' && (content as ChoiceScene).phoneMailPreview) return;
    if (SceneExecutor.isPhoneMailInteract(content)) return;
    setIsProfileOpen(prev => !prev);
  }, [game]);

  const handleToastClose = useCallback(() => {
    setToastMessage(null);
  }, []);

  const handleReviewSubmit = useCallback(
    async (body: string) => {
      if (!user || !game.currentScene) {
        throw new Error('Session invalide');
      }
      const currentScene = game.currentScene;
      await createReview({
        scenario_id: scenarioId,
        scene_id: currentScene.id,
        mission_id: activeMissionId ?? null,
        body,
        mission_label: missionLabel ?? null,
        chapter_label: getChapterLabelForScene(scenario, currentScene.id, activeMissionId) ?? null,
        scene_excerpt: getSceneExcerptWithSpeaker(currentScene) ?? null,
      });
      setToastMessage('Commentaire envoyé — merci !');
    },
    [user, game.currentScene, scenarioId, activeMissionId, missionLabel, scenario]
  );

  const showReviewWidget = !previewMode && canSubmitReview(user) && Boolean(user && game.currentScene);
  const gameKeyboardActive =
    !isMenuOpen &&
    !isProfileOpen &&
    !isDeveloperMenuOpen &&
    !isTextOverlayOpen &&
    !isMissionOpeningOpen &&
    !activeChapterEnd &&
    !isReviewModalOpen;

  const handleRestart = useCallback(() => {
    setIsEnded(false);
    setEndingMessage('');
    game.initializeGame(scenarioId, scenario, {
      force: true,
      startScene: missionStartScene,
      missionId: activeMissionId,
    });
  }, [game, scenarioId, scenario, missionStartScene, activeMissionId]);

  const handleChapterEndRestart = useCallback(() => {
    clearChapterEndBridgeTimers();
    setChapterEndBridgePhase('idle');
    setIsTextOverlayOpen(false);
    setCurrentTextOverlay(null);
    handleRestart();
  }, [handleRestart, clearChapterEndBridgeTimers]);

  const handleChapterEndMenu = useCallback(() => {
    handleMenuToggle();
  }, [handleMenuToggle]);

  /** Carte de progression lue : le bilan prend le relais (la navigation a déjà eu lieu). */
  const handleChapterProgressContinue = useCallback(() => {
    clearChapterEndBridgeTimers();
    chapterEndBridgeCaptionRef.current = null;
    setChapterEndBridgePhase((phase) => (phase === 'black-hold' ? 'summary-in' : phase));
    setChapterProgressAcknowledged(true);
  }, [clearChapterEndBridgeTimers]);

  const handleAchievementComplete = useCallback(() => {
    game.setAchievementAnimating(false);
  }, [game]);

  // Calculate achievement target position when animation starts
  useEffect(() => {
    if (game.gameState?.isAchievementAnimating && profileTargetRef.current) {
      const rect = profileTargetRef.current.getBoundingClientRect();
      setAchievementTargetPos({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    }
  }, [game.gameState?.isAchievementAnimating]);

  // Handle TextOverlay detection (layout effect so chapter-end never paints the previous video frame)
  useLayoutEffect(() => {
    const content = game.getCurrentContent();
    const chapterEnd = resolveChapterEnd(content);
    if (chapterEnd) {
      setActiveChapterEnd((prev) => prev ?? chapterEnd);
      setActiveGameOver(null);
      setActiveRecordCard(null);
      setCurrentTextOverlay(null);
      setIsTextOverlayOpen(false);
      return;
    }
    setActiveChapterEnd(null);
    if (SceneExecutor.isTextOverlay(content)) {
      setActiveGameOver(null);
      setActiveRecordCard(null);
      setCurrentTextOverlay(content);
      setIsTextOverlayOpen(true);
      return;
    }
    if (!isSceneReady) return;
    if (SceneExecutor.isGameOver(content)) {
      setActiveGameOver(content);
      setActiveRecordCard(null);
      setCurrentTextOverlay(null);
      setIsTextOverlayOpen(false);
      return;
    }
    setActiveGameOver(null);
    if (SceneExecutor.isRecordCard(content)) {
      setActiveRecordCard(content);
      setCurrentTextOverlay(null);
      setIsTextOverlayOpen(false);
      return;
    }
    setActiveRecordCard(null);
    setCurrentTextOverlay(null);
    setIsTextOverlayOpen(false);
  }, [game.currentScene?.id, isSceneReady, game]);

  // Count each fall for the leaderboard: a game over reached while playing, not a save reopened on it.
  const previousSceneIdRef = useRef<string | null>(null);
  useEffect(() => {
    const scene = game.currentScene;
    const previousSceneId = previousSceneIdRef.current;
    previousSceneIdRef.current = scene?.id ?? null;
    if (scene && isNewGameOver(previousSceneId, scene)) {
      reportGameOver({ scenarioId, missionId: game.gameState?.missionId, sceneId: scene.id });
    }
  }, [game.currentScene, game.gameState?.missionId, scenarioId]);

  // Handle TimedOverlay detection
  useEffect(() => {
    const content = game.getCurrentContent();
    if (isSceneReady && SceneExecutor.isTimedOverlay(content)) {
      const timedContent = content as TimedOverlayScene;
      setCurrentTimedOverlay(timedContent);
      const timer = setTimeout(() => {
        setCurrentTimedOverlay(null);
        if (timedContent.goto) {
          game.navigateToScene(timedContent.goto);
        } else {
          game.advanceDialogue();
        }
      }, timedContent.durationMs);
      return () => clearTimeout(timer);
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.currentScene?.id, isSceneReady]);

  const processTextOverlayActions = useCallback(async (actions: CommandItem[]) => {
    // Apply all commands to game state using the unified executeCommands logic
    game.applyCommands(actions);

    // Show notifications for key events
    for (const action of actions) {
      switch (action.command) {
        case 'unlockSkill':
          const skill = scenario.metadata.skills?.find(s => s.id === action.target);
          if (skill) {
            setToastMessage(`Skill unlocked: ${skill.name}`);
          }
          break;
        case 'unlockAchievement':
          const achievement = scenario.metadata.achievements.find(a => a.id === action.target);
          if (achievement) {
            setToastMessage(`Achievement unlocked: ${achievement.name}`);
          }
          break;
        case 'collectItem':
          const item = scenario.items.find(i => i.id === action.target);
          if (item) {
            setToastMessage(`Objet collecté: ${item.name}`);
          }
          break;
      }
      
      if (actions.length > 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
  }, [game, scenario.metadata.skills, scenario.metadata.achievements, scenario.items]);

  const handleTextOverlayOptionClick = useCallback((optionIndex: number) => {
    unlockGameAudio();
    if (game.gameState?.isAchievementAnimating) return;

    // Use currentTextOverlay if available, fall back to currentContent
    const content = game.getCurrentContent();
    const overlayData = currentTextOverlay || (SceneExecutor.isTextOverlay(content) ? content : null);
    
    if (!overlayData) return;

    const option = resolveTextOverlayOptions(overlayData)[optionIndex];
    if (!option) return;

    // Execute actions if present
    if (option.actions && option.actions.length > 0) {
      processTextOverlayActions(option.actions);
    }

    // Navigate to scene if goto is specified
    if (option.goto) {
      setIsTextOverlayOpen(false);
      setCurrentTextOverlay(null);
      game.navigateToScene(option.goto);
      return;
    }

    // Default behavior: close overlay and advance dialogue
    if (option.closeOverlay !== false) {
      setIsTextOverlayOpen(false);
      setCurrentTextOverlay(null);
      
      // Only advance dialogue if this overlay was the current dialogue content
      if (SceneExecutor.isTextOverlay(content)) {
        game.advanceDialogue();
      }
    }
  }, [game, currentTextOverlay, processTextOverlayActions]);

  const handleGameOverRestart = useCallback(() => {
    unlockGameAudio();
    if (!activeGameOver) return;
    if (activeGameOver.actions?.length) {
      processTextOverlayActions(activeGameOver.actions);
    }
    setActiveGameOver(null);
    if (activeGameOver.goto) {
      game.navigateToScene(activeGameOver.goto);
    }
  }, [activeGameOver, game, processTextOverlayActions]);

  const handleRecordCardContinue = useCallback(() => {
    unlockGameAudio();
    if (!activeRecordCard) return;
    setActiveRecordCard(null);
    if (activeRecordCard.goto) {
      game.navigateToScene(activeRecordCard.goto);
    } else {
      game.advanceDialogue();
    }
  }, [activeRecordCard, game]);

  const handleChapterEndContinue = useCallback(() => {
    unlockGameAudio();
    if (game.gameState?.isAchievementAnimating || chapterEndExiting) return;

    const currentSceneId = game.currentScene?.id;
    if (!activeChapterEnd || !currentSceneId) return;

    setChapterEndExiting(true);
    setTimeout(() => {
      if (activeChapterEnd.continueActions?.length) {
        processTextOverlayActions(activeChapterEnd.continueActions);
      }

      const isMissionEnd = activeChapterEnd.data.missionIndex === activeChapterEnd.data.missionTotal;
      const missionId = game.gameState?.missionId;
      if (isMissionEnd && missionId && game.gameState) {
        const missions = scenario.metadata.missions ?? [];
        const mission = missions.find((entry) => entry.id === missionId);
        const liveCounts = readLiveCategoryCounts(game.gameState.variables);
        const store = loadPedaCampaignStore(scenarioId);
        const earned = mergeCampaignEarnedCounts(
          store,
          missions,
          missionId,
          mission?.order ?? 1,
          liveCounts,
        );
        const possible = collectPedaShieldsByCategory(scenario, mission?.order ?? 1);
        const rank = resolvePedaRank(
          sumPedaCategoryCounts(earned),
          sumPedaCategoryCounts(possible),
        );
        flushLiveMissionToCampaign(scenarioId, missionId, liveCounts, rank.percent);
        const characterIds = (scenario.metadata.characters ?? []).map((character) => character.id);
        const liveTrust = readLiveTrustCounts(game.gameState.variables, characterIds);
        flushLiveMissionTrustToCampaign(scenarioId, missionId, liveTrust);
        reportMissionResult({ scenarioId, missionId, liveCounts, liveTrust });
      }

      clearChapterEndBridgeTimers();
      setChapterEndBridgePhase('idle');
      setIsTextOverlayOpen(false);
      setCurrentTextOverlay(null);
      setActiveChapterEnd(null);
      setChapterEndExiting(false);

      const nextId = resolveNextSceneId(scenario, currentSceneId);
      if (nextId) {
        setIsTransitioning(true);
        setTimeout(() => {
          game.navigateToScene(nextId);
          setIsTransitioning(false);
        }, 350);
        return;
      }

      setEndingMessage('Félicitations ! Vous avez terminé cette aventure.');
      setIsEnded(true);
      game.markScenarioCompleted();
    }, 420);
  }, [
    game,
    scenario,
    activeChapterEnd,
    chapterEndExiting,
    processTextOverlayActions,
    clearChapterEndBridgeTimers,
    scenarioId,
  ]);

  const handleTextOverlayClose = useCallback(() => {
    if (game.gameState?.isAchievementAnimating) return;
    setIsTextOverlayOpen(false);
    setCurrentTextOverlay(null);
    
    // Only advance dialogue if this overlay was the current dialogue content
    const content = game.getCurrentContent();
    if (SceneExecutor.isTextOverlay(content)) {
      game.advanceDialogue();
    }
  }, [game]);

  const handleTitleCardContinue = useCallback(() => {
    unlockGameAudio();
    if (game.gameState?.isAchievementAnimating) return;
    setIsTextOverlayOpen(false);
    setCurrentTextOverlay(null);
    game.advanceDialogue();
    // Defer so state has advanced to the choice before handleChoice runs
    setTimeout(() => handleChoice(0), 0);
  }, [game, handleChoice]);

  const handlePhoneChatComplete = useCallback((goto?: string) => {
    const content = game.getCurrentContent();
    if (SceneExecutor.isPhoneChat(content)) {
      const targetScene = goto ?? content.goto;
      if (targetScene) {
        setIsTransitioning(true);
        setTimeout(() => {
          game.navigateToScene(targetScene);
          setIsTransitioning(false);
        }, 300);
      } else {
        game.advanceDialogue();
      }
    }
  }, [game]);

  const handlePhoneCallComplete = useCallback((goto?: string) => {
    const content = game.getCurrentContent();
    if (SceneExecutor.isPhoneCall(content)) {
      const targetScene = goto ?? content.goto;
      if (targetScene) {
        setIsTransitioning(true);
        setTimeout(() => {
          game.navigateToScene(targetScene);
          setIsTransitioning(false);
        }, 300);
      } else {
        game.advanceDialogue();
      }
    }
  }, [game]);

  const handlePhoneMailComplete = useCallback(() => {
    const content = game.getCurrentContent();
    if (SceneExecutor.isPhoneMail(content)) {
      const gotoTarget = content.goto;
      if (gotoTarget) {
        setIsTransitioning(true);
        setTimeout(() => {
          game.navigateToScene(gotoTarget);
          setIsTransitioning(false);
        }, 300);
      } else {
        game.advanceDialogue();
      }
    }
  }, [game]);

  const navigateAfterMailTriage = useCallback((targetScene: string) => {
    setIsTransitioning(true);
    setTimeout(() => {
      game.navigateToScene(targetScene);
      setIsTransitioning(false);
    }, 300);
  }, [game]);

  const completeObjectInspection = useCallback((choice: 'report' | 'dismiss', targetOverride?: SceneElement) => {
    const target = targetOverride ?? inspectTarget;
    if (!target) return;
    const elementId = target.id;
    const clickAction = target.clickAction;
    const dismissAction = target.dismissAction;
    const scene = game.currentScene;
    const isObservationScene = scene?.type === 'observation';

    const applyInspectionFlags = () => {
      game.setFlag(getInspectedFlag(elementId), true);
      if (choice === 'report') {
        game.setFlag(getReportedFlag(elementId), true);
      }
    };

    const runClickAction = () => {
      if (isObservationScene) {
        const { reportMessage, dismissMessage } = resolveElementMessages(target);
        const message = choice === 'report' ? reportMessage : dismissMessage;
        if (message.trim()) {
          setToastMessage(message);
        }
      } else if (choice === 'report' && clickAction) {
        processClickAction(clickAction, elementId);
      } else if (choice === 'dismiss' && dismissAction) {
        processClickAction(dismissAction, elementId);
      }
    };

    const bumpInspectGate = (flagsOverride?: Record<string, boolean>) => {
      const flags = flagsOverride ?? game.gameState?.flags ?? {};
      if (isObservationScene) {
        syncObservationGate(scene, flags, game.setFlag);
        if (allAnomaliesInspected(scene, flags)) {
          const dialogues = getObservationCompletionDialogues(scene);
          dialogues.forEach((dialogue, index) => {
            setTimeout(() => setToastMessage(dialogue), (index + 1) * 2800);
          });
        }
      } else {
        syncObjectInspectGate(scene, game.evaluateCondition, game.setFlag);
      }
      setObjectInspectGateTick((tick) => tick + 1);
    };

    const buildUpdatedFlags = (): Record<string, boolean> => ({
      ...(game.gameState?.flags ?? {}),
      [getInspectedFlag(elementId)]: true,
      ...(choice === 'report' ? { [getReportedFlag(elementId)]: true } : {}),
    });

    const finishInspection = () => {
      const updatedFlags = buildUpdatedFlags();
      applyInspectionFlags();
      runClickAction();
      bumpInspectGate(updatedFlags);
      setInspectTarget(null);
    };

    const hasRewardingReports = scene?.elements?.some(
      (element) => element.type === 'object' && (element.pedaShields ?? 0) > 0
    );

    if (choice === 'report') {
      const shieldsEarned = target.pedaShields ?? 0;
      const objectCategory = normalizePedaCategory(target.pedaCategory);
      if (shieldsEarned > 0) {
        if (objectCategory) emitPedaEvent(objectCategory, true);
        const updatedFlags = buildUpdatedFlags();
        applyInspectionFlags();
        setInspectTarget(null);
        runClickAction();
        bumpInspectGate(updatedFlags);
        beginShieldGainAnimation(
          shieldsEarned,
          () => undefined,
          objectCategory ? getPedaCategoryLabel(objectCategory) : 'Compétence cyber',
          objectCategory,
        );
        return;
      }

      if (WRONG_ANSWER_FLASH_ENABLED && hasRewardingReports) {
        const rewarding = scene?.elements?.find(
          (element) => element.type === 'object' && (element.pedaShields ?? 0) > 0
        );
        const rewardCategory = normalizePedaCategory(rewarding?.pedaCategory);
        if (rewardCategory) emitPedaEvent(rewardCategory, false);
        setWrongAnswerFlashKey((k) => k + 1);
      }
    } else if (
      WRONG_ANSWER_FLASH_ENABLED
      && hasRewardingReports
      && (target.pedaShields ?? 0) > 0
    ) {
      const objectCategory = normalizePedaCategory(target.pedaCategory);
      if (objectCategory) emitPedaEvent(objectCategory, false);
      setWrongAnswerFlashKey((k) => k + 1);
    }

    finishInspection();
  }, [game, inspectTarget, processClickAction, beginShieldGainAnimation, emitPedaEvent]);

  const handleObservationZoneClick = useCallback((elementId: string) => {
    if (inspectTarget) return;
    const scene = game.currentScene;
    if (!scene || scene.type !== 'observation') return;
    if (game.gameState?.flags?.[getInspectedFlag(elementId)]) return;
    const element = scene.elements?.find((item) => item.id === elementId);
    if (!element) return;
    setInspectTarget(element);
  }, [game, inspectTarget]);

  const handleObservationExit = useCallback(() => {
    const scene = game.currentScene;
    if (!scene || scene.type !== 'observation') return;
    const target = scene.goto ?? resolveNextSceneId(scenario, scene.id);
    if (!target) return;
    setIsTransitioning(true);
    setTimeout(() => {
      game.navigateToScene(target);
      setIsTransitioning(false);
    }, 300);
  }, [game, scenario]);

  const handleClickableScreenExit = useCallback(() => {
    const scene = game.currentScene;
    if (!scene || scene.type !== 'clickableScreen') return;
    const target = resolveNextSceneId(scenario, scene.id);
    if (!target) return;
    setIsTransitioning(true);
    setTimeout(() => {
      game.navigateToScene(target);
      setIsTransitioning(false);
    }, 300);
  }, [game, scenario]);

  const handleClickableZone = useCallback(async (zone: ClickableScreenZone) => {
    unlockGameAudio();
    if (
      game.gameState?.isAchievementAnimating
      || clickedClickableZoneId
      || isTransitioning
      || isShieldAnimOpen
      || isConfidenceOverlayOpen
    ) {
      return;
    }

    const content = game.getCurrentContent();
    if (!content || content.type !== 'clickableScreen') return;

    setClickedClickableZoneId(zone.id);
    await new Promise((resolve) => setTimeout(resolve, 300));

    const option = clickableZoneToChoiceOption(zone);
    const proceedZone = () => {
      setIsTransitioning(true);
      setTimeout(() => {
        game.makeClickableZoneChoice(zone);
        setClickedClickableZoneId(null);
        setIsTransitioning(false);
      }, 300);
    };

    const validCharacterIds = new Set((scenario.metadata.characters ?? []).map((c) => c.id));
    const overlayCharacters: Array<{ characterId: string; confidenceLevel: number }> = [];
    const overlayStartLevels: Record<string, number> = {};

    const resolvedConfidenceGains = resolveChoiceConfidenceGains(
      option.confidenceGains,
      game.getConfidence,
      validCharacterIds,
    );
    for (const gain of resolvedConfidenceGains) {
      overlayCharacters.push({
        characterId: gain.characterId,
        confidenceLevel: gain.targetLevel,
      });
      overlayStartLevels[gain.characterId] = gain.startLevel;
    }

    const finishZoneFlow = () => {
      if (overlayCharacters.length > 0 && !isConfidenceOverlayOpen) {
        beginConfidenceGainAnimation(
          overlayCharacters,
          overlayStartLevels,
          proceedZone,
        );
        return;
      }
      proceedZone();
    };

    const shieldsEarned = option.pedaShields ?? 0;
    const sceneRewardCategory = getClickableScreenRewardCategory(content);
    const hasRewardingZone = getClickableScreenMaxPedaShields(content) > 0;
    if (shieldsEarned > 0) {
      const optionCategory = normalizePedaCategory(option.pedaCategory) ?? sceneRewardCategory;
      if (optionCategory) emitPedaEvent(optionCategory, true);
    } else if (hasRewardingZone && sceneRewardCategory) {
      emitPedaEvent(sceneRewardCategory, false);
    }
    if (
      WRONG_ANSWER_FLASH_ENABLED
      && shieldsEarned === 0
      && hasRewardingZone
    ) {
      setWrongAnswerFlashKey((k) => k + 1);
    }

    if (shieldsEarned > 0 && !isShieldAnimOpen) {
      const optionCategory = normalizePedaCategory(option.pedaCategory) ?? sceneRewardCategory;
      beginShieldGainAnimation(
        shieldsEarned,
        finishZoneFlow,
        optionCategory ? getPedaCategoryLabel(optionCategory) : 'Compétence cyber',
        optionCategory,
      );
      return;
    }

    finishZoneFlow();
  }, [
    game,
    scenario.metadata.characters,
    clickedClickableZoneId,
    isTransitioning,
    isShieldAnimOpen,
    isConfidenceOverlayOpen,
    beginShieldGainAnimation,
    beginConfidenceGainAnimation,
    unlockGameAudio,
    emitPedaEvent,
  ]);

  const handleSituationSortShieldGain = useCallback((payload: {
    amount: number;
    subtitle?: string;
    onDone: () => void;
  }) => {
    if (isShieldAnimOpen) return;
    const category = normalizePedaCategory(payload.subtitle);
    beginShieldGainAnimation(
      payload.amount,
      payload.onDone,
      category ? getPedaCategoryLabel(category) : 'Compétence cyber',
      category,
    );
  }, [isShieldAnimOpen, beginShieldGainAnimation]);

  const handleSituationSortRetry = useCallback((payload: {
    total: number;
    byCategory: Partial<PedaCategoryCounts>;
  }) => {
    if (payload.total <= 0) return;
    const variables = { ...(game.gameState?.variables ?? {}) };
    variables.peda_shields = Math.max(0, (variables.peda_shields ?? 0) - payload.total);
    for (const [category, amount] of Object.entries(payload.byCategory) as Array<[PedaCategoryKey, number]>) {
      if (!amount || amount <= 0) continue;
      const key = pedaCategoryVariableKey(category);
      variables[key] = Math.max(0, (variables[key] ?? 0) - amount);
    }
    game.setVariable('peda_shields', variables.peda_shields ?? 0);
    for (const [category, amount] of Object.entries(payload.byCategory) as Array<[PedaCategoryKey, number]>) {
      if (!amount || amount <= 0) continue;
      game.setVariable(pedaCategoryVariableKey(category), variables[pedaCategoryVariableKey(category)] ?? 0);
    }
  }, [game]);

  const handlePhoneMailTriageAction = useCallback((
    action: PhoneMailTriageAction,
    context?: PhoneMailTriageContext
  ): boolean => {
    if (game.gameState?.isAchievementAnimating || isShieldAnimOpen) return false;
    const content = game.getCurrentContent();
    if (!SceneExecutor.isPhoneMail(content)) return false;

    const shieldsEarned = action.pedaShields ?? 0;
    const actionCategory = normalizePedaCategory(action.pedaCategory);
    if (shieldsEarned > 0) {
      setPendingShieldGain((prev) => prev + shieldsEarned);
      if (actionCategory) {
        pendingShieldByCategoryRef.current[actionCategory] = (
          pendingShieldByCategoryRef.current[actionCategory] ?? 0
        ) + shieldsEarned;
        setPendingShieldSubtitle(getPedaCategoryLabel(actionCategory));
        emitPedaEvent(actionCategory, true);
      }
    }

    if (action.actions?.length) {
      game.applyCommands(action.actions);
    }

    const isMultiMail = Boolean(content.mails?.length);
    if (shieldsEarned === 0) {
      let rewardCategory: PedaCategoryKey | null = null;
      if (isMultiMail && context?.mailId && content.mails) {
        const mail = content.mails.find((m) => m.id === context.mailId);
        const rewarding = mail?.triageActions.find((a) => (a.pedaShields ?? 0) > 0);
        rewardCategory = normalizePedaCategory(rewarding?.pedaCategory);
      } else {
        const rewarding = content.triageActions?.find((a) => (a.pedaShields ?? 0) > 0);
        rewardCategory = normalizePedaCategory(rewarding?.pedaCategory);
      }
      if (rewardCategory) {
        emitPedaEvent(rewardCategory, false);
        if (WRONG_ANSWER_FLASH_ENABLED) {
          setWrongAnswerFlashKey((k) => k + 1);
        }
      }
    }
    const allTriaged = Boolean(context?.allTriaged);

    if (isMultiMail) {
      const steps = resolveTriageReactionSteps(action);
      if (steps.length) {
        setMailTriageAllTriaged(allTriaged);
        setMailTriageReactionSteps(steps);
        setMailTriageStepIndex(0);
        setMailTriageReactionIndex(0);
        setMailTriagePendingNav(action.goto ?? null);
        const firstStep = steps[0];
        setMailTriageHighlightInEmail(firstStep?.highlightInEmail ?? []);
        setMailTriageReactionsUnlocked(!triageStepHasHighlight(firstStep));
        return true;
      }
      if (action.goto) {
        navigateAfterMailTriage(action.goto);
        return true;
      }
      if (allTriaged) {
        if (content.goto) {
          navigateAfterMailTriage(content.goto);
        } else {
          game.advanceDialogue();
        }
      }
      return true;
    }

    if (action.goto) {
      setIsTransitioning(true);
      setTimeout(() => {
        game.navigateToScene(action.goto!);
        setIsTransitioning(false);
      }, 300);
    }
    return true;
  }, [game, navigateAfterMailTriage, isShieldAnimOpen, emitPedaEvent]);

  const finishMailTriageStep = useCallback(() => {
    const targetScene = mailTriagePendingNav;
    const allTriaged = mailTriageAllTriaged;
    setMailTriageReactionSteps(null);
    setMailTriageStepIndex(0);
    setMailTriageReactionIndex(0);
    setMailTriagePendingNav(null);
    setMailTriageHighlightInEmail([]);
    setMailTriageReactionsUnlocked(false);
    setMailTriageAllTriaged(false);
    if (targetScene) {
      navigateAfterMailTriage(targetScene);
      return;
    }
    if (allTriaged) {
      const content = game.getCurrentContent();
      if (SceneExecutor.isPhoneMail(content)) {
        if (content.goto) {
          navigateAfterMailTriage(content.goto);
        } else {
          game.advanceDialogue();
        }
        return;
      }
    }
    setMailInboxReturnSignal((n) => n + 1);
  }, [mailTriagePendingNav, mailTriageAllTriaged, navigateAfterMailTriage, game]);

  const handleMailTriageReactionClick = useCallback(() => {
    if (!mailTriageReactionSteps || isShieldAnimOpen) return;
    const currentStep = mailTriageReactionSteps[mailTriageStepIndex];
    if (!currentStep) return;

    if (mailTriageReactionIndex < currentStep.reactions.length - 1) {
      runNarrativeUiFade(() => setMailTriageReactionIndex((i) => i + 1));
      return;
    }

    if (mailTriageStepIndex < mailTriageReactionSteps.length - 1) {
      const nextStepIndex = mailTriageStepIndex + 1;
      const nextStep = mailTriageReactionSteps[nextStepIndex];
      runNarrativeUiFade(() => {
        setMailTriageStepIndex(nextStepIndex);
        setMailTriageReactionIndex(0);
        setMailTriageHighlightInEmail(nextStep?.highlightInEmail ?? []);
        setMailTriageReactionsUnlocked(!triageStepHasHighlight(nextStep));
      });
      return;
    }

    const shieldsToAnimate = pendingShieldGain;
    const subtitle = pendingShieldSubtitle;
    const categoryGains = { ...pendingShieldByCategoryRef.current };
    setPendingShieldGain(0);
    setPendingShieldSubtitle(null);
    pendingShieldByCategoryRef.current = {};
    setMailTriageReactionSteps(null);
    setMailTriageStepIndex(0);
    setMailTriageReactionIndex(0);
    setMailTriageHighlightInEmail([]);
    setMailTriageReactionsUnlocked(false);
    if (shieldsToAnimate > 0) {
      beginShieldGainAnimation(
        shieldsToAnimate,
        finishMailTriageStep,
        subtitle ?? 'Compétence cyber',
        categoryGains,
      );
      return;
    }
    finishMailTriageStep();
  }, [
    mailTriageReactionSteps,
    mailTriageStepIndex,
    mailTriageReactionIndex,
    isShieldAnimOpen,
    pendingShieldGain,
    pendingShieldSubtitle,
    beginShieldGainAnimation,
    finishMailTriageStep,
    runNarrativeUiFade,
  ]);

  const handleMailTriageHighlightZoomComplete = useCallback(() => {
    setMailTriageReactionsUnlocked(true);
  }, []);

  useEffect(() => {
    if (!mailTriageReactionSteps || mailTriageReactionsUnlocked) return;
    const step = mailTriageReactionSteps[mailTriageStepIndex];
    if (!triageStepHasHighlight(step)) return;
    const fallbackMs = EMAIL_ZOOM_FLY_MS * 2 + 200;
    const timer = window.setTimeout(() => {
      setMailTriageReactionsUnlocked(true);
    }, fallbackMs);
    return () => window.clearTimeout(timer);
  }, [
    mailTriageReactionSteps,
    mailTriageStepIndex,
    mailTriageReactionsUnlocked,
    mailTriageHighlightInEmail,
  ]);

  const handleShieldGainArrive = useCallback(() => {
    if (shieldAppliedRef.current) return;
    shieldAppliedRef.current = true;
    const amount = shieldAnimAmount;
    const categoryGains = { ...shieldAnimCategoriesRef.current };
    shieldAnimCategoriesRef.current = {};
    if (amount > 0) {
      const variables = { ...(game.gameState?.variables ?? {}) };
      variables.peda_shields = (variables.peda_shields ?? 0) + amount;
      for (const [category, categoryAmount] of Object.entries(categoryGains) as Array<[PedaCategoryKey, number]>) {
        if (!categoryAmount || categoryAmount <= 0) continue;
        const key = pedaCategoryVariableKey(category);
        variables[key] = (variables[key] ?? 0) + categoryAmount;
      }
      game.setVariable('peda_shields', variables.peda_shields ?? 0);
      for (const [category, categoryAmount] of Object.entries(categoryGains) as Array<[PedaCategoryKey, number]>) {
        if (!categoryAmount || categoryAmount <= 0) continue;
        game.setVariable(
          pedaCategoryVariableKey(category),
          variables[pedaCategoryVariableKey(category)] ?? 0,
        );
      }
      setShieldHudPulse(true);
      window.setTimeout(() => setShieldHudPulse(false), 300);
    }
  }, [game, shieldAnimAmount]);

  const handleShieldGainComplete = useCallback(() => {
    handleShieldGainArrive();
    setIsShieldAnimOpen(false);
    setShieldAnimAmount(0);
    const next = postShieldActionRef.current;
    postShieldActionRef.current = null;
    next?.();
  }, [handleShieldGainArrive]);

  const handlePasswordGameComplete = useCallback((result: {
    stars: number;
    totalRounds: number;
    action: 'restart' | 'menu' | 'next';
  }) => {
    const content = game.getCurrentContent();
    if (!SceneExecutor.isPasswordGame(content)) return;

    game.setVariable('password_game_stars', result.stars);

    if (result.action === 'menu') {
      const menuScene = content.gotoMenu || '__MAIN_MENU__';
      game.navigateToScene(menuScene);
    } else if (result.action === 'next') {
      game.advanceDialogue();
    } else {
      game.advanceDialogue();
    }
  }, [game]);

  const handleDragDropGameComplete = useCallback((result: {
    stars: number;
    totalStars: number;
    action: 'restart' | 'menu' | 'next';
  }) => {
    const content = game.getCurrentContent();
    if (!SceneExecutor.isDragDropGame(content)) return;

    game.setVariable('dragdrop_game_stars', result.stars);

    if (result.action === 'menu') {
      const menuScene = content.gotoMenu || '__MAIN_MENU__';
      game.navigateToScene(menuScene);
    } else if (result.action === 'restart') {
      const restartScene = content.gotoRestart;
      if (restartScene) {
        game.navigateToScene(restartScene);
      } else {
        game.advanceDialogue();
      }
    } else if (result.action === 'next') {
      game.advanceDialogue();
    } else {
      game.advanceDialogue();
    }
  }, [game]);

  const handleSituationSortGameComplete = useCallback((result: {
    correctCount: number;
    total: number;
    action: 'restart' | 'menu' | 'next';
  }) => {
    const content = game.getCurrentContent();
    if (!SceneExecutor.isSituationSortGame(content)) return;

    game.setVariable('situation_sort_score', result.correctCount);

    if (result.action === 'menu') {
      const menuScene = content.gotoMenu || '__MAIN_MENU__';
      game.navigateToScene(menuScene);
    } else if (result.action === 'restart') {
      const restartScene = content.gotoRestart;
      if (restartScene) {
        game.navigateToScene(restartScene);
      } else {
        game.advanceDialogue();
      }
    } else if (result.action === 'next') {
      game.advanceDialogue();
    } else {
      game.advanceDialogue();
    }
  }, [game]);

  const handleConfidenceGainContinue = useCallback(() => {
    if (game.gameState?.isAchievementAnimating) return;

    const confidenceData = currentConfidenceGain || (SceneExecutor.isConfidenceGain(game.getCurrentContent()) ? game.getCurrentContent() as ConfidenceGainScene : null);
    const isChoiceTriggered = confidenceData?.id === CHOICE_CONFIDENCE_SCENE_ID;

    if (confidenceData) {
      confidenceData.characters.forEach(char => {
        game.setConfidence(char.characterId, char.confidenceLevel);
      });

      if (isChoiceTriggered) {
        setIsConfidenceOverlayOpen(false);
        setCurrentConfidenceGain(null);
        setConfidenceStartLevels({});
        const next = postConfidenceActionRef.current;
        postConfidenceActionRef.current = null;
        next?.();
        return;
      }

      const hasNextScene = Boolean(confidenceData.goto);

      if (!hasNextScene && !game.hasMoreContent()) {
        setIsConfidenceOverlayOpen(false);
        setCurrentConfidenceGain(null);
        setEndingMessage('Félicitations ! Vous avez terminé cette aventure.');
        setIsEnded(true);
        game.markScenarioCompleted();
        return;
      }

      if (confidenceData.goto) {
        setIsConfidenceOverlayOpen(false);
        setCurrentConfidenceGain(null);
        game.navigateToScene(confidenceData.goto);
        return;
      }
    }

    setIsConfidenceOverlayOpen(false);
    setCurrentConfidenceGain(null);
    setConfidenceStartLevels({});

    if (SceneExecutor.isConfidenceGain(game.getCurrentContent())) {
      game.advanceDialogue();
    }
  }, [game, currentConfidenceGain]);

  // useMemo must be called before any early returns to respect React hooks rules
  const achievements = useMemo(() => {
    const achievementsData = scenario?.metadata.achievements || [];
    return achievementsData.map(achievement => ({
      ...achievement,
      icon: achievement.icon
        ? resolveScenarioAssetUrl(
            achievement.icon,
            scenarioId,
            getScenarioAssetCacheKey(scenario),
          )
        : undefined
    }));
  }, [scenario?.metadata.achievements, scenario?.metadata.version, scenarioId]);

  const chapterConfidenceGains = useMemo(() => {
    const characterIds = (scenario.metadata.characters ?? []).map((c) => c.id);
    const rawGains = computeChapterConfidenceGains(game.gameState?.variables ?? {}, characterIds);
    return rawGains.map((gain) => {
      const portrait = prefixedCharacterData.find((c) => c.id === gain.characterId);
      return {
        ...gain,
        name: portrait?.name ?? gain.characterId,
        portraitImage: portrait?.defaultImage,
      };
    });
  }, [game.gameState?.variables, scenario.metadata.characters, prefixedCharacterData]);

  const allyTrustRows = useMemo((): AllyTrustDisplayRow[] => {
    if (!activeChapterEnd || !game.gameState) return [];
    const missions = scenario.metadata.missions ?? [];
    const characters = scenario.metadata.characters ?? [];
    const characterIds = characters.map((character) => character.id);
    if (!characterIds.length) return [];

    const activeMissionId = resolveActiveMissionId({
      missionId,
      gameStateMissionId: game.gameState.missionId,
      missions,
      chapters: scenario.metadata.chapters,
      scenes: scenario.scenes,
      currentSceneId: game.gameState.currentSceneId,
    });
    const currentMission = missions.find((entry) => entry.id === activeMissionId)
      ?? missions.slice().sort((a, b) => a.order - b.order)[0];
    if (!currentMission) return [];

    const store = loadTrustCampaignStore(scenarioId);
    const liveCounts = readLiveTrustCounts(game.gameState.variables, characterIds);
    const totals = mergeCampaignTrustCounts(
      store,
      missions,
      currentMission.id,
      currentMission.order,
      liveCounts,
    );

    return buildAllyTrustDisplayRows({
      totals,
      characters,
      trustLabels: scenario.metadata.trustLabels,
      resolvePortrait: (character) => {
        const portrait = prefixedCharacterData.find((entry) => entry.id === character.id);
        return portrait?.icon ?? portrait?.defaultImage;
      },
    });
  }, [
    activeChapterEnd,
    game.gameState,
    missionId,
    prefixedCharacterData,
    scenario,
    scenarioId,
  ]);

  const pedaEndStats = useMemo(() => {
    if (!activeChapterEnd || !game.gameState) return null;
    const currentSceneId = game.gameState.currentSceneId;
    if (!currentSceneId) return null;
    return buildPedaEndStats({
      scenario,
      currentSceneId,
      liveCounts: readLiveCategoryCounts(game.gameState.variables),
      chapterStartCounts: readChapterStartCategoryCounts(game.gameState.variables),
    });
  }, [
    activeChapterEnd,
    game.gameState,
    scenario,
  ]);

  const inventoryContentType = game.currentScene && game.gameState
    ? game.getCurrentContent()?.type
    : undefined;
  const inventoryDisabled = Boolean(
    inventoryContentType
    && (
      inventoryContentType === 'observation'
      || inventoryContentType === 'passwordGame'
      || inventoryContentType === 'dragDropGame'
      || inventoryContentType === 'situationSortGame'
      || inventoryContentType === 'timedOverlay'
    ),
  );
  const inventorySections = useMemo(
    () => (isInventoryOpen ? game.getInventorySections() : []),
    [game, isInventoryOpen, game.inventoryStore],
  );
  const resolveInventoryImage = useCallback(
    (path: string | undefined) => {
      if (!path) return undefined;
      return resolveScenarioAssetUrl(
        path,
        scenarioId,
        getScenarioAssetCacheKey(scenario),
      );
    },
    [scenario, scenarioId],
  );
  const currentInventoryUnlockNotice = inventoryUnlockQueue[0] ?? null;
  const currentInventoryUnlockNoticeImageUrl = useMemo(
    () => resolveInventoryImage(currentInventoryUnlockNotice?.image),
    [resolveInventoryImage, currentInventoryUnlockNotice?.image],
  );
  const handleInventoryToggle = useCallback(() => {
    if (inventoryDisabled) return;
    setIsInventoryOpen((prev) => {
      if (!prev) {
        setInventoryHasUnread(false);
      }
      return !prev;
    });
    setIsMenuOpen(false);
    setIsProfileOpen(false);
  }, [inventoryDisabled]);
  const handleInventoryClose = useCallback(() => {
    setIsInventoryOpen(false);
    setInventoryHasUnread(false);
  }, []);
  const handleInventoryUnlockNoticeEnd = useCallback(() => {
    setInventoryUnlockQueue((queue) => queue.slice(1));
    setInventoryUnlockNoticeKey((key) => key + 1);
  }, []);
  const handleInventoryUnreadReceived = useCallback(() => {
    setInventoryHasUnread(true);
  }, []);
  const handleInventoryTileOpen = useCallback(
    (entryId: string) => {
      game.markInventoryOpened(entryId);
    },
    [game],
  );
  const handleInventoryOpenFullCard = useCallback(
    (sceneId: string) => {
      const scene = scenario.scenes.find((item) => item.id === sceneId);
      if (!scene || scene.type !== 'recordCard' || scene.recordKind !== 'attaque') return;
      setInventoryFullCardExiting(false);
      setInventoryFullCard(scene as RecordCardScene);
    },
    [scenario.scenes],
  );
  const handleInventoryFullCardClose = useCallback(() => {
    if (inventoryFullCardExiting) return;
    if (isMotionReduced()) {
      setInventoryFullCard(null);
      setInventoryFullCardExiting(false);
      return;
    }
    setInventoryFullCardExiting(true);
  }, [inventoryFullCardExiting]);
  const handleInventoryEscapeKey = useCallback(() => {
    if (inventoryFullCard) {
      handleInventoryFullCardClose();
      return;
    }
    handleInventoryClose();
  }, [inventoryFullCard, handleInventoryFullCardClose, handleInventoryClose]);
  const handleInventoryFullCardOverlayAnimationEnd = useCallback(
    (event: React.AnimationEvent<HTMLDivElement>) => {
      if (!inventoryFullCardExiting) return;
      if (event.target !== event.currentTarget) return;
      if (!event.animationName.includes('inventoryFullCardOverlayOut')) return;
      setInventoryFullCard(null);
      setInventoryFullCardExiting(false);
    },
    [inventoryFullCardExiting],
  );

  if (game.error) {
    return (
      <ErrorScreen
        title="Gameplay Error"
        message="An error occurred during gameplay"
        details={game.error}
        onRetry={handleRestart}
        onBack={onBackToMenu}
      />
    );
  }

  if (!game.currentScene || !game.gameState) {
    return <div className={styles.loading}>Loading game...</div>;
  }

  const currentContent = game.getCurrentContent();
  const isUnknownScene = Boolean(currentContent) && !isKnownSceneType(currentContent.type);
  const currentSceneId = game.gameState.currentSceneId;
  const displayScene = game.currentScene
    ? { ...game.currentScene, ...resolveVisualState(scenario, currentSceneId) }
    : null;
  const isChoice = game.isAtChoice();
  const choicePhonePreview = isChoice && currentContent?.type === 'choice'
    ? (currentContent as ChoiceScene).phoneMailPreview
    : undefined;
  const isBlockingMailChoice = Boolean(choicePhonePreview);
  const isPhoneMailOpenWait = SceneExecutor.isPhoneMailInteract(currentContent)
    && Boolean(currentContent.waitForOpen);
  const directorMailFields = getDirectorMailFields(game.currentScene);
  const isDirectorMailFlowScene = sceneHasPhoneMailInteract(game.currentScene);
  const directorMailPhoneActive = isDirectorMailFlowScene
    && Boolean(directorMailFields)
    && !directorMailClosing
    && SceneExecutor.resolvePhoneStateTarget(scenario, currentSceneId) >= 1;
  const directorMailControlledPhase: MailPhase = directorMailReading
    ? 'reading'
    : directorMailInboxReached
      ? 'inbox'
      : 'menu';
  const usePhoneChoiceBackdrop = isBlockingMailChoice
    || isPhoneMailOpenWait
    || (directorMailPhoneActive && currentContent?.type === 'dialogue');
  const phoneTarget = SceneExecutor.resolvePhoneStateTarget(scenario, currentSceneId);
  const isPhoneCallContent = SceneExecutor.isPhoneCall(currentContent);
  const isPhoneStyleCallContent = isPhoneCallContent && !isInlinePhoneCallStyle(currentContent);
  const phoneCallContent = isPhoneCallContent ? currentContent : null;
  const isInlinePhoneCallActive = Boolean(
    phoneCallContent && isInlinePhoneCallStyle(phoneCallContent)
  );
  const isPhoneChatContent = SceneExecutor.isPhoneChat(currentContent);
  const isPhoneMailContent = SceneExecutor.isPhoneMail(currentContent);
  const showDirectorMailPhone = directorMailPhoneActive
    && Boolean(directorMailFields)
    && !directorMailClosing
    && !isPhoneChatContent
    && !isPhoneCallContent
    && !isPhoneMailContent;
  const showMailPreviewPhone = isBlockingMailChoice && Boolean(choicePhonePreview);
  const hasPhoneAppContent = Boolean(
    isPhoneChatContent
    || (isPhoneStyleCallContent && phoneCallContent)
    || isPhoneMailContent
    || showDirectorMailPhone
    || showMailPreviewPhone
  );
  const phoneUiState: PhoneState = hasPhoneAppContent
    ? 'active'
    : phoneTarget === 1
      ? 'idle'
      : 'hidden';
  const isObjectSearch = isObjectSearchScene(game.currentScene);
  const observationScene = currentContent?.type === 'observation'
    ? (currentContent as ObservationScene)
    : null;
  const isObservation = Boolean(observationScene);
  const observationGateOpen = isObservation
    && game.evaluateCondition(`flag:${getObservationGateFlag(observationScene!)}`);
  const observationExitPromptText = observationScene?.exitPrompt ?? '';
  const clickableScreenScene = currentContent?.type === 'clickableScreen'
    ? (currentContent as ClickableScreenScene)
    : null;
  const visibleClickableZones = clickableScreenScene
    ? getVisibleClickableZones(clickableScreenScene, game.evaluateCondition)
    : [];
  const showClickableInstruction = Boolean(clickableScreenScene) && visibleClickableZones.length > 0;
  const showClickableScreenExit = Boolean(clickableScreenScene) && visibleClickableZones.length === 0;
  const clickableInstructionText = clickableScreenScene?.instruction ?? '';
  const explorationChoice = isChoice && currentContent?.type === 'choice'
    ? (currentContent as ChoiceScene)
    : null;
  const choicePromptText = explorationChoice?.prompt;
  const precedingDialogue = isChoice && currentSceneId && !isObjectSearch
    && isGenericChoicePrompt(choicePromptText)
    ? findPrecedingDialogue(scenario, currentSceneId)
    : null;
  const stickyChoiceDialogue = precedingDialogue
    ? {
        speaker: precedingDialogue.speaker,
        text: game.interpolateText(precedingDialogue.text),
        isInternal: Boolean(precedingDialogue.isInternal),
      }
    : null;
  const visibleExplorationOptions = explorationChoice?.options.filter(
    (option) =>
      !option.stopButton
      && (!option.condition || game.evaluateCondition(option.condition))
  ) ?? [];
  const showExplorationInstruction = isObjectSearch
    && Boolean(explorationChoice)
    && visibleExplorationOptions.length === 0;
  const explorationInstructionText = explorationChoice?.prompt
    || "Qu'est-ce qui cloche ? Cliquez sur les éléments suspects.";
  const nextSceneForTitleCard = (() => {
    if (!currentSceneId) return null;
    const nextId = resolveNextSceneId(scenario, currentSceneId);
    return nextId ? SceneExecutor.getSceneById(scenario, nextId) : null;
  })();
  const isTitleCardOverlay = currentContent?.type === 'textOverlay'
    && currentContent.overlayType === 'poster'
    && nextSceneForTitleCard?.type === 'choice'
    && nextSceneForTitleCard.options?.length === 1;
  
  // Get skills and achievements data
  const skills = scenario?.metadata.skills || [];
  const unlockedSkills = game.gameState?.unlockedSkills || [];
  const unlockedAchievements = game.gameState?.unlockedAchievements || [];

  // Determine active overlay (prefer click-set state, fall back to dialogue content)
  const activeOverlay = currentTextOverlay || (currentContent?.type === 'textOverlay' ? currentContent as TextOverlayScene : null);
  const isChapterEndOverlay = activeChapterEnd !== null;
  const isLetterSkinOverlay = isMissionOpeningOpen || Boolean(
    isTextOverlayOpen
    && activeOverlay
    && !isTitleCardOverlay
    && !isChapterEndOverlay
    && resolveTextOverlayType(activeOverlay.overlayType) === 'letter'
  );
  // Lettre d'ouverture de mission écrite dans le scénario : bandeau calculé + frise.
  const letterMissionOpening = isLetterSkinOverlay && !isMissionOpeningOpen && activeOverlay?.id === currentSceneId
    ? missionOpeningFrise
    : null;
  const isChapterEndActive = activeChapterEnd !== null;
  const showChapterProgressCard = Boolean(
    activeChapterEnd
    && chapterEndFrise
    && !chapterProgressAcknowledged
    && chapterEndBridgePhase !== 'scene-exit'
    && !chapterEndExiting
  );
  const showChapterEndSummaryCard = Boolean(
    activeChapterEnd
    && (chapterProgressAcknowledged || !chapterEndFrise)
    && (chapterEndExiting
      || (isChapterEndActive
        && chapterEndBridgePhase !== 'scene-exit'
        && chapterEndBridgePhase !== 'black-hold'))
  );
  const showChapterEndLayer = showChapterProgressCard
    || showChapterEndSummaryCard
    || chapterEndBridgePhase === 'black-hold'
    || chapterEndBridgePhase === 'summary-in';
  const chapterProgressBackground = showChapterProgressCard
    && displayScene?.background
    && !isVideoAsset(displayScene.background)
    ? resolveScenarioAssetUrl(displayScene.background, scenarioId, getScenarioAssetCacheKey(scenario))
    : undefined;
  const showGameplayScene = !isChapterEndActive || chapterEndBridgePhase === 'scene-exit';
  const hideDialogueForChapterEndBridge = chapterEndBridgePhase === 'scene-exit';
  const titleCardChoiceOption = isTitleCardOverlay && nextSceneForTitleCard?.type === 'choice'
    ? nextSceneForTitleCard.options[0]
    : null;
  const activeMissionMax = scenario.metadata.missions?.find(
    (mission) => mission.id === game.gameState?.missionId
  )?.pedaShieldsMax;
  const pedaShields = (game.gameState.variables?.peda_shields as number) || 0;
  const pedaShieldsMax = (game.gameState.variables?.peda_shields_max as number)
    || activeMissionMax
    || (scenario.metadata.initialVariables?.peda_shields_max)
    || (SceneExecutor.isPhoneMail(currentContent) ? (currentContent as PhoneMailScene).pedaShieldsMax : undefined)
    || 0;
  const showShieldHud = pedaShieldsMax > 0;
  const hideHudForChapterEnd = activeChapterEnd !== null;
  const hideHudForGameOver = activeGameOver !== null;
  const inventoryUnlockFxPaused = hideHudForChapterEnd || hideHudForGameOver;
  const chapterShieldsStart = (game.gameState.variables?.peda_shields_chapter_start as number) ?? 0;
  const chapterShieldsEarned = Math.max(0, pedaShields - chapterShieldsStart);
  const showStopButton = isSceneReady && !isEnded && !activeGameOver && !activeRecordCard && !showChapterEndLayer
    && !(SceneExecutor.isSituationSortGame(currentContent));
  const isSituationSortActive = isSceneReady && SceneExecutor.isSituationSortGame(currentContent);
  const stopButtonDisabled = isTransitioning
    || isShieldAnimOpen
    || isConfidenceOverlayOpen
    || Boolean(game.gameState?.isAchievementAnimating)
    || isMenuOpen
    || isProfileOpen
    || isInventoryOpen
    || isDeveloperMenuOpen
    || isTextOverlayOpen
    || isMissionOpeningOpen
    || Boolean(stopFeedbackText);

  return (
    <div
      className={`${styles.gameplayScreen} ${previewMode ? styles.gameplayScreenPreview : ''} ${showReviewWidget ? styles.gameplayScreenAvecRevue : ''}`}
    >
      <PhoneGameplayImmersiveOverlays />
      <div className={styles.mainContent}>
        {showGameDeveloperMenu && isDeveloperMenuOpen && (
          <DeveloperMenu
            scenario={developerScenario}
            currentSceneId={game.currentScene?.id}
            onNavigateToScene={handleDeveloperNavigateToScene}
            onClose={() => setIsDeveloperMenuOpen(false)}
          />
        )}

        {showChapterEndLayer && (
          <div className={`${styles.chapterEndInterstitial} ${
            chapterEndExiting
              ? styles.chapterEndInterstitialExiting
              : chapterEndBridgePhase === 'black-hold' || chapterEndBridgePhase === 'summary-in'
                ? ''
                : styles.chapterEndInterstitialEntering
          }`}>
            {showChapterProgressCard && chapterEndFrise && (
              <div className={styles.chapterProgressFrame}>
                <ChapterProgressCard
                  frise={chapterEndFrise}
                  backgroundUrl={chapterProgressBackground}
                  onContinue={handleChapterProgressContinue}
                />
              </div>
            )}
            {chapterEndBridgePhase === 'black-hold' && !chapterEndFrise && (
              <div className={styles.chapterEndBridgeStage} aria-hidden>
                <div className={styles.chapterEndBridgeGoldLine} />
                {(chapterEndBridgeCaptionRef.current || activeChapterEnd?.data.eyebrow) && (
                  <div className={styles.chapterEndBridgeCaption}>
                    {chapterEndBridgeCaptionRef.current || activeChapterEnd?.data.eyebrow}
                  </div>
                )}
              </div>
            )}
            {showChapterEndSummaryCard && activeChapterEnd && (
              <ChapterEndScreen
                data={activeChapterEnd.data}
                chapterShieldsEarned={chapterShieldsEarned}
                chapterConfidenceGains={chapterConfidenceGains}
                continueText={activeChapterEnd.continueText}
                evaluateCondition={game.evaluateCondition}
                pedaEndStats={pedaEndStats}
                allyTrustRows={allyTrustRows}
                onContinue={handleChapterEndContinue}
                onRestart={handleChapterEndRestart}
                onMenu={handleChapterEndMenu}
                exiting={chapterEndExiting}
                transparentOverlay={chapterEndBridgePhase === 'summary-in'}
              />
            )}
          </div>
        )}

        {chapterEndBridgePhase === 'scene-exit' && (
          <div className={styles.chapterEndBridgeVignette} aria-hidden />
        )}

        {showGameplayScene && (
        <div
          ref={sceneContainerRef}
          className={`${styles.sceneContainer} ${
            isLetterSkinOverlay ? styles.sceneContainerFront : ''
          } ${
            chapterEndBridgePhase === 'scene-exit'
              ? styles.sceneChapterEndExit
              : isTransitioning
                ? styles.fadeOut
                : styles.fadeIn
          }`}
        >
          <SceneRenderer
            ref={sceneRendererRef}
            scene={displayScene ?? game.currentScene}
            characters={prefixedCharacterData}
            onElementClick={handleElementClick}
            onMailboxAction={handleMailboxAction}
            inventory={game.gameState.inventory}
            activeCharacterId={
              currentContent?.type === 'dialogue'
                ? resolveActiveCharacterElementId(currentContent)
                : null
            }
            isDialogueActive={currentContent?.type === 'dialogue'}
            inspectModalOpen={Boolean(inspectTarget)}
          />

          {inspectTarget?.image && (
            <ObjectInspectOverlay
              imageSrc={inspectTarget.image}
              alt={inspectTarget.id}
              onReport={() => completeObjectInspection('report')}
              onDismiss={() => completeObjectInspection('dismiss')}
            />
          )}

          {isSceneReady && observationScene && (
            <ObservationOverlay
              key={`observation-${observationScene.id}-${objectInspectGateTick}`}
              scene={observationScene}
              flags={game.gameState?.flags ?? {}}
              instruction={observationScene.instruction ?? "Qu'est-ce qui cloche ? Cliquez sur les éléments suspects."}
              exitPrompt={observationExitPromptText}
              exitChoiceText={observationScene.exitChoice?.text ?? 'Continuer'}
              gateOpen={observationGateOpen}
              evaluateCondition={game.evaluateCondition}
              inspectModalOpen={Boolean(inspectTarget)}
              disabled={
                isTransitioning
                || isShieldAnimOpen
                || isConfidenceOverlayOpen
                || Boolean(game.gameState?.isAchievementAnimating)
              }
              onZoneClick={handleObservationZoneClick}
              onExit={handleObservationExit}
            />
          )}

          {isSceneReady && clickableScreenScene && visibleClickableZones.length > 0 && (
            <ClickableScreenOverlay
              scene={clickableScreenScene}
              zones={visibleClickableZones}
              onZoneClick={handleClickableZone}
              clickedZoneId={clickedClickableZoneId}
              disabled={isTransitioning || isShieldAnimOpen || isConfidenceOverlayOpen}
            />
          )}

          <Phone
            state={phoneUiState}
            imageSrc={SHARED_ASSETS.telephone}
            withChoiceLayout={usePhoneChoiceBackdrop}
          >
            {isPhoneChatContent && currentContent && (
              <PhoneChat
                contactName={(currentContent as PhoneChatScene).contactName}
                contactAvatar={resolveScenarioAssetUrl(
                  (currentContent as PhoneChatScene).contactAvatar,
                  scenarioId,
                  getScenarioAssetCacheKey(scenario),
                )}
                messages={(currentContent as PhoneChatScene).messages}
                messageDelay={(currentContent as PhoneChatScene).messageDelay}
                typingDelay={(currentContent as PhoneChatScene).typingDelay}
                replyOptions={(currentContent as PhoneChatScene).replyOptions}
                onComplete={handlePhoneChatComplete}
              />
            )}
            {isPhoneStyleCallContent && phoneCallContent && (
              <PhoneCall
                key={`${game.currentScene?.id}-call`}
                contactName={phoneCallContent.contactName}
                contactAvatar={resolveScenarioAssetUrl(
                  phoneCallContent.contactAvatar,
                  scenarioId,
                  getScenarioAssetCacheKey(scenario),
                )}
                messages={phoneCallContent.messages}
                messageDelay={phoneCallContent.messageDelay}
                incomingCall={phoneCallContent.incomingCall}
                autoAnswer={phoneCallContent.autoAnswer}
                allowDecline={phoneCallContent.allowDecline}
                ringDurationMs={phoneCallContent.ringDurationMs}
                ringAfterDeclineMs={phoneCallContent.ringAfterDeclineMs}
                replyOptions={phoneCallContent.replyOptions}
                embeddedInScenario
                onComplete={handlePhoneCallComplete}
              />
            )}
            {isPhoneMailContent && currentContent && (
              <PhoneMail
                key={game.currentScene?.id ?? 'phone-mail'}
                from={(currentContent as PhoneMailScene).from}
                subject={(currentContent as PhoneMailScene).subject}
                content={(currentContent as PhoneMailScene).content}
                preview={(currentContent as PhoneMailScene).preview}
                receivedAt={(currentContent as PhoneMailScene).receivedAt}
                attachments={(currentContent as PhoneMailScene).attachments}
                attachmentsNote={(currentContent as PhoneMailScene).attachmentsNote}
                unreadCount={(currentContent as PhoneMailScene).unreadCount}
                continueLabel={(currentContent as PhoneMailScene).continueLabel}
                startInReading={(currentContent as PhoneMailScene).startInReading}
                mailProgress={(currentContent as PhoneMailScene).mailProgress}
                inboxItems={(currentContent as PhoneMailScene).inboxItems}
                triageActions={(currentContent as PhoneMailScene).triageActions}
                mails={(currentContent as PhoneMailScene).mails}
                reactionsActive={Boolean(mailTriageReactionSteps?.length)}
                triageDisabled={!isSceneReady}
                highlightInEmail={mailTriageHighlightInEmail}
                inboxReturnSignal={mailInboxReturnSignal}
                overlayPortalTarget={overlayPortalTarget}
                onComplete={handlePhoneMailComplete}
                onTriageAction={handlePhoneMailTriageAction}
                onHighlightZoomComplete={handleMailTriageHighlightZoomComplete}
                highlightZoomSessionKey={
                  mailTriageReactionSteps
                  && triageStepHasHighlight(mailTriageReactionSteps[mailTriageStepIndex])
                    ? `${mailTriageStepIndex}:${JSON.stringify(mailTriageHighlightInEmail)}`
                    : ''
                }
              />
            )}
            {showDirectorMailPhone && directorMailFields && (
              <PhoneMail
                key="director-mail-app"
                from={directorMailFields.from}
                subject={directorMailFields.subject}
                content={directorMailFields.content}
                preview={directorMailFields.preview}
                receivedAt={directorMailFields.receivedAt}
                attachments={directorMailFields.attachments}
                attachmentsNote={directorMailFields.attachmentsNote}
                unreadCount={directorMailFields.unreadCount ?? 1}
                continueLabel={directorMailFields.continueLabel ?? 'Fermer'}
                controlledPhase={directorMailControlledPhase}
                onPhaseChange={handleDirectorMailPhaseChange}
                allowOpenMail={isPhoneMailOpenWait && !directorMailReading}
                onMailOpen={handleDirectorMailOpen}
                inboxLocked={isBlockingMailChoice}
                onComplete={directorMailReading ? handleDirectorMailReadingComplete : undefined}
              />
            )}
            {showMailPreviewPhone && choicePhonePreview && (
              <PhoneMail
                key="choice-mail-preview"
                from={choicePhonePreview.from}
                subject={choicePhonePreview.subject}
                preview={choicePhonePreview.preview}
                receivedAt={choicePhonePreview.receivedAt}
                unreadCount={choicePhonePreview.unreadCount ?? 1}
                previewOnly
              />
            )}
          </Phone>

          {isSceneReady && isInlinePhoneCallActive && phoneCallContent && (
            <InlineCall
              key={`${game.currentScene?.id}-inline-call`}
              contactName={phoneCallContent.contactName}
              contactRole={phoneCallContent.contactRole}
              contactAvatar={resolveScenarioAssetUrl(
                phoneCallContent.contactAvatar,
                scenarioId,
                getScenarioAssetCacheKey(scenario),
              )}
              messages={phoneCallContent.messages}
              messageDelay={phoneCallContent.messageDelay}
              incomingCall={phoneCallContent.incomingCall}
              replyOptions={phoneCallContent.replyOptions}
              onComplete={handlePhoneCallComplete}
              keyboardActive={gameKeyboardActive}
            />
          )}

          {isSceneReady && SceneExecutor.isPasswordGame(currentContent) && (
            <PasswordGame
              rounds={(currentContent as PasswordGameScene).rounds}
              victoryReward={(currentContent as PasswordGameScene).victoryReward}
              screenPosition={(currentContent as PasswordGameScene).screenPosition}
              characterData={prefixedCharacterData}
              hasNextScene={game.hasMoreContent()}
              onComplete={handlePasswordGameComplete}
            />
          )}

          {isSceneReady && SceneExecutor.isDragDropGame(currentContent) && (
            <DragDropGame
              phrases={(currentContent as DragDropGameScene).phrases}
              requiredSelections={(currentContent as DragDropGameScene).requiredSelections}
              resultRules={(currentContent as DragDropGameScene).resultRules}
              victoryReward={(currentContent as DragDropGameScene).victoryReward}
              victorySubReward={(currentContent as DragDropGameScene).victorySubReward}
              starsTotal={(currentContent as DragDropGameScene).starsTotal}
              screenPosition={(currentContent as DragDropGameScene).screenPosition}
              speakerName={(currentContent as DragDropGameScene).speakerName}
              characterData={prefixedCharacterData}
              hasNextScene={game.hasMoreContent()}
              onComplete={handleDragDropGameComplete}
            />
          )}

          {isSceneReady && SceneExecutor.isSituationSortGame(currentContent) && (
            <SituationSortGame
              categories={(currentContent as SituationSortGameScene).categories}
              situations={(currentContent as SituationSortGameScene).situations}
              instructionText={(currentContent as SituationSortGameScene).instructionText}
              speakerName={(currentContent as SituationSortGameScene).speakerName}
              speakerCharacterId={(currentContent as SituationSortGameScene).speakerCharacterId}
              requireAllCorrect={(currentContent as SituationSortGameScene).requireAllCorrect}
              screenPosition={(currentContent as SituationSortGameScene).screenPosition}
              characterData={prefixedCharacterData}
              hasNextScene={game.hasMoreContent()}
              isShieldGainActive={isShieldAnimOpen}
              onShieldGain={handleSituationSortShieldGain}
              onPedaAttempt={({ pedaCategory, correct }) => {
                const category = normalizePedaCategory(pedaCategory);
                if (category) emitPedaEvent(category, correct);
              }}
              onRetry={handleSituationSortRetry}
              onHudProgressChange={setSortGameHudProgress}
              onComplete={handleSituationSortGameComplete}
            />
          )}

          {mailTriageReactionSteps && mailTriageReactionsUnlocked && createPortal(
            <div
              className={
                overlayPortalsContained
                  ? `${styles.mailTriageDialogueLayer} ${styles.mailTriageDialogueLayerContained}`
                  : styles.mailTriageDialogueLayer
              }
            >
              <QuickFadePresence show={narrativeUiVisible}>
                <DialogueBox
                  text={game.interpolateText(
                    mailTriageReactionSteps[mailTriageStepIndex]?.reactions[mailTriageReactionIndex] ?? ''
                  )}
                  onClick={handleMailTriageReactionClick}
                  showContinue={
                    mailTriageReactionIndex
                      < (mailTriageReactionSteps[mailTriageStepIndex]?.reactions.length ?? 0) - 1
                    || mailTriageStepIndex < mailTriageReactionSteps.length - 1
                    || Boolean(mailTriagePendingNav)
                  }
                  isInternal
                  characterData={prefixedCharacterData}
                  keyboardActive={gameKeyboardActive}
                />
              </QuickFadePresence>
            </div>,
            overlayPortalRoot
          )}

          {isSceneReady && !hideDialogueForChapterEndBridge && !mailTriageReactionSteps && !isChoice && currentContent?.type === 'dialogue' && !isMoodChanging && (
            <QuickFadePresence show={narrativeUiVisible}>
              <DialogueBox
                speaker={currentContent.speaker}
                text={game.interpolateText(currentContent.text)}
                onClick={handleDialogueClick}
                showContinue={
                  game.hasMoreContent()
                  || Boolean(currentContent.type === 'dialogue' && currentContent.goto)
                }
                characterId={currentContent.characterId}
                emotion={currentContent.emotion}
                isInternal={currentContent.isInternal}
                characterData={prefixedCharacterData}
                highlightZone={(game.currentScene as { feedbackHighlight?: string } | null)?.feedbackHighlight === 'autre' ? 'autre' : undefined}
                keyboardActive={gameKeyboardActive}
              />
            </QuickFadePresence>
          )}

          {isSceneReady && !hideDialogueForChapterEndBridge && !mailTriageReactionSteps && isUnknownScene && (
            <QuickFadePresence show={narrativeUiVisible}>
              <DialogueBox
                text={formatUnknownSceneTypeMessage(currentContent?.type)}
                onClick={handleUnknownSceneContinue}
                showContinue
                isInternal
                characterData={prefixedCharacterData}
                keyboardActive={gameKeyboardActive}
              />
            </QuickFadePresence>
          )}

          {isSceneReady && (showExplorationInstruction || showClickableInstruction) && (
            <QuickFadePresence show={narrativeUiVisible}>
              <DialogueBox
                text={
                  showClickableInstruction
                    ? clickableInstructionText
                    : explorationInstructionText
                }
                onClick={() => undefined}
                showContinue={false}
                isInternal
                characterData={prefixedCharacterData}
                keyboardActive={false}
              />
            </QuickFadePresence>
          )}

          {isSceneReady && showClickableScreenExit && clickableScreenScene && (
            <QuickFadePresence show={narrativeUiVisible}>
              <ChoicePanel
                key={`clickable-screen-exit-${clickableScreenScene.id}`}
                prompt=""
                options={[{ text: 'Continuer' }]}
                onChoice={() => { handleClickableScreenExit(); }}
                evaluateCondition={() => true}
                compact
              />
            </QuickFadePresence>
          )}

          {isSceneReady && isChoice && currentContent?.type === 'choice' && !showExplorationInstruction && (
            <QuickFadePresence show={narrativeUiVisible}>
              <ChoicePanel
                key={isObjectSearch ? `explore-${visibleExplorationOptions.length}-${objectInspectGateTick}` : 'choice'}
                prompt={currentContent.prompt}
                options={currentContent.options}
                onChoice={handleChoice}
                evaluateCondition={game.evaluateCondition}
                compact={isObjectSearch}
                elevated={isBlockingMailChoice}
                stickyDialogue={stickyChoiceDialogue}
              />
            </QuickFadePresence>
          )}

          {showStopButton && (
            <StopButton onClick={handleStopClick} disabled={stopButtonDisabled} />
          )}

          {stopFeedbackText && (
            <DialogueBox
              text={stopFeedbackText}
              onClick={() => setStopFeedbackText(null)}
              showContinue={false}
              isInternal
              characterData={prefixedCharacterData}
              keyboardActive={!isMenuOpen && !isProfileOpen && !isDeveloperMenuOpen && !isTextOverlayOpen && !isReviewModalOpen}
            />
          )}

          {isSceneReady && isTitleCardOverlay && activeOverlay && (
            <>
              <div className={styles.titleCardOverlay} aria-live="polite">
                <div className={styles.titleCardText}>
                  {game.interpolateText(activeOverlay.content)}
                </div>
              </div>
              {titleCardChoiceOption && (
                <div className={styles.titleCardDialogueBar}>
                  <button
                    type="button"
                    className={styles.titleCardContinueBtn}
                    onClick={handleTitleCardContinue}
                  >
                    {titleCardChoiceOption.text}
                  </button>
                </div>
              )}
            </>
          )}

          {isTextOverlayOpen && activeOverlay && !isTitleCardOverlay && !isChapterEndOverlay && (
            <TextOverlay
              type={resolveTextOverlayType(activeOverlay.overlayType)}
              title={activeOverlay.title}
              channelLabel={activeOverlay.channelLabel}
              characterName={activeOverlay.characterName}
              characterIcon={activeOverlay.characterIcon}
              content={activeOverlay.content}
              stars={activeOverlay.stars}
              reward={activeOverlay.reward}
              sections={activeOverlay.sections}
              options={resolveTextOverlayOptions(activeOverlay)}
              metadata={activeOverlay.metadata}
              enableTyping={activeOverlay.enableTyping}
              missionOpening={letterMissionOpening}
              onOptionClick={handleTextOverlayOptionClick}
              onClose={handleTextOverlayClose}
            />
          )}

          {isMissionOpeningOpen && generatedOpening && missionOpeningFrise && (
            <TextOverlay
              type="letter"
              title={generatedOpening.title}
              content=""
              options={[{ text: 'Commencer la mission' }]}
              missionOpening={missionOpeningFrise}
              onOptionClick={closeGeneratedOpening}
              onClose={closeGeneratedOpening}
            />
          )}

          {activeGameOver && (
            <GameOverScreen
              heading={activeGameOver.heading}
              text={game.interpolateText(activeGameOver.text)}
              restartLabel={activeGameOver.restartLabel}
              stamp={activeGameOver.stamp}
              background={
                activeGameOver.background
                  ? resolveScenarioAssetUrl(
                      activeGameOver.background,
                      scenarioId,
                      getScenarioAssetCacheKey(scenario),
                    )
                  : undefined
              }
              onRestart={handleGameOverRestart}
            />
          )}

          {activeRecordCard && (() => {
            const bgPath = activeRecordCard.background ?? displayScene?.background;
            const resolvedBackground = bgPath
              ? resolveScenarioAssetUrl(
                  bgPath,
                  scenarioId,
                  getScenarioAssetCacheKey(scenario),
                )
              : undefined;
            const resolveRecordAsset = (path: string | undefined) => (
              path
                ? resolveScenarioAssetUrl(
                    path,
                    scenarioId,
                    getScenarioAssetCacheKey(scenario),
                  )
                : undefined
            );

            if (activeRecordCard.recordKind === 'attaque') {
              return (
                <AttackCardScreen
                  {...mapAttackCardSceneToProps(activeRecordCard, {
                    interpolate: game.interpolateText.bind(game),
                    resolveAsset: resolveRecordAsset,
                    background: resolvedBackground,
                    onContinue: handleRecordCardContinue,
                  })}
                />
              );
            }

            const serialDisplay = resolveRecordCardSerialDisplay(activeRecordCard.serial);
            const portraitPath = activeRecordCard.image
              ?? (activeRecordCard.characterId
                ? resolveCharacterPortrait(scenario.metadata.characters, activeRecordCard.characterId)?.defaultImage
                : undefined);
            return (
              <RecordCardScreen
                title={game.interpolateText(activeRecordCard.title)}
                subtitle={activeRecordCard.subtitle ? game.interpolateText(activeRecordCard.subtitle) : undefined}
                summary={activeRecordCard.summary ? game.interpolateText(activeRecordCard.summary) : undefined}
                portraitSrc={resolveRecordAsset(portraitPath)}
                matricule={activeRecordCard.matricule ? game.interpolateText(activeRecordCard.matricule) : undefined}
                serialSerie={serialDisplay.serie}
                serialNumber={serialDisplay.number}
                dangerScore={activeRecordCard.danger?.score}
                dangerMax={activeRecordCard.danger?.max}
                dangerWord={activeRecordCard.danger?.word ? game.interpolateText(activeRecordCard.danger.word) : undefined}
                taken={activeRecordCard.taken?.map((item) => game.interpolateText(item))}
                saved={activeRecordCard.saved?.map((item) => game.interpolateText(item))}
                stamp={activeRecordCard.stamp}
                continueText={activeRecordCard.continueText}
                nextHint={activeRecordCard.nextHint ? game.interpolateText(activeRecordCard.nextHint) : undefined}
                background={resolvedBackground}
                onContinue={handleRecordCardContinue}
              />
            );
          })()}
        </div>
        )}

        <PersistentUI
          achievementProgress={game.getAchievementProgress()}
          onMenuClick={handleMenuToggle}
          onProfileClick={handleProfileToggle}
          isMenuOpen={isMenuOpen}
          isProfileOpen={isProfileOpen}
          inventory={game.gameState.inventory}
          items={scenario.items}
          unlockedSkills={unlockedSkills}
          skills={skills}
          achievements={achievements}
          unlockedAchievements={unlockedAchievements}
          chapters={missionChapters}
          missionLabel={missionLabel}
          onNavigateToChapter={game.navigateToChapter}
          onBack={onBackToMenu}
          onRestart={handleRestart}
          profileRef={profileTargetRef}
          profileTheme={scenario.metadata.profileTheme}
          characters={charactersWithConfidence}
          showShieldHud={showShieldHud && !isInventoryOpen}
          showTopHud={!hideHudForChapterEnd && !hideHudForGameOver && !isInventoryOpen}
          showBackButton={!isInventoryOpen && !isSituationSortActive}
          showToolIcons={!isInventoryOpen && !isSituationSortActive}
          hudAboveOverlay={isInventoryOpen}
          hudProgressLabel={isSituationSortActive ? sortGameHudProgress : null}
          showInventoryButton={game.hasInventoryCatalog}
          inventoryDisabled={inventoryDisabled}
          onInventoryClick={handleInventoryToggle}
          inventoryUnlockNotice={currentInventoryUnlockNotice}
          inventoryUnlockNoticeKey={inventoryUnlockNoticeKey}
          inventoryUnlockNoticeImageUrl={currentInventoryUnlockNoticeImageUrl}
          inventoryUnlockFxPaused={inventoryUnlockFxPaused}
          inventoryHasUnread={inventoryHasUnread}
          onInventoryUnlockNoticeEnd={handleInventoryUnlockNoticeEnd}
          onInventoryUnreadReceived={handleInventoryUnreadReceived}
          shieldHudRef={shieldHudRef}
          pedaShields={pedaShields}
          pedaShieldsMax={pedaShieldsMax}
          shieldHudPulse={shieldHudPulse}
          musicEnabled={musicEnabled}
          onMusicToggle={toggleMusic}
          preview={previewMode}
        />

        {isInventoryOpen && game.hasInventoryCatalog && (
          <InventoryOverlay
            sections={inventorySections}
            entries={scenario.metadata.inventory?.entries}
            scenes={scenario.scenes}
            characters={scenario.metadata.characters}
            resolveImage={resolveInventoryImage}
            formatText={game.interpolateText.bind(game)}
            onClose={handleInventoryClose}
            onEscapeKey={handleInventoryEscapeKey}
            onTileOpen={handleInventoryTileOpen}
            onOpenFullCard={handleInventoryOpenFullCard}
            unlockedIds={game.inventoryStore.unlocked}
          />
        )}

        {inventoryFullCard && (
          <div
            className={[
              styles.inventoryFullCardOverlay,
              inventoryFullCardExiting
                ? styles.inventoryFullCardOverlayExiting
                : styles.inventoryFullCardOverlayEntering,
            ].join(' ')}
            onAnimationEnd={handleInventoryFullCardOverlayAnimationEnd}
          >
            <div
              className={[
                styles.inventoryFullCardContent,
                inventoryFullCardExiting ? styles.inventoryFullCardContentExiting : '',
              ].filter(Boolean).join(' ')}
            >
              <AttackCardScreen
                {...mapAttackCardSceneToProps(inventoryFullCard, {
                  interpolate: game.interpolateText.bind(game),
                  resolveAsset: (path) => (
                    path
                      ? resolveScenarioAssetUrl(
                          path,
                          scenarioId,
                          getScenarioAssetCacheKey(scenario),
                        )
                      : undefined
                  ),
                  instant: true,
                  onContinue: handleInventoryFullCardClose,
                })}
                continueText="Fermer"
                nextHint={undefined}
              />
            </div>
          </div>
        )}

        {toastMessage && (
          <ToastNotification
            message={toastMessage}
            onClose={handleToastClose}
          />
        )}

        {isEnded && (
          <EndScreen
            endingMessage={endingMessage}
            storyTitle={scenario.metadata.title}
            onReturnToMenu={onBackToMenu}
            onRestart={handleRestart}
          />
        )}

        {game.gameState?.isAchievementAnimating && (
          <AchievementCelebration
            achievement={scenario.metadata.achievements.find(a => a.id === game.gameState?.lastUnlockedAchievementId)}
            targetX={achievementTargetPos.x}
            targetY={achievementTargetPos.y}
            onComplete={handleAchievementComplete}
          />
        )}

        {isConfidenceOverlayOpen && currentConfidenceGain && (
          <ConfidenceGainOverlay
            characters={currentConfidenceGain.characters}
            characterData={prefixedCharacterData}
            introText={currentConfidenceGain.introText}
            continueText={currentConfidenceGain.continueText}
            startLevels={confidenceStartLevels}
            trustLabels={scenario.metadata.trustLabels}
            onContinue={handleConfidenceGainContinue}
          />
        )}

        {isShieldAnimOpen && shieldAnimAmount > 0 && (
          <ShieldGainOverlay
            amount={shieldAnimAmount}
            targetRef={shieldHudRef}
            subtitle={shieldAnimSubtitle}
            onArrive={handleShieldGainArrive}
            onComplete={handleShieldGainComplete}
          />
        )}

        {WRONG_ANSWER_FLASH_ENABLED && !a11yStrict && (
          <WrongAnswerFlash
            trigger={wrongAnswerFlashKey}
            shakeTargetRef={sceneContainerRef}
          />
        )}

        {currentTimedOverlay && (
          <div className={styles.timedOverlay}>
            <div className={`${styles.timedOverlayFrame} cn-zoom-ui`}>
              <span className={styles.timedOverlayMessage}>{currentTimedOverlay.message}</span>
              <div className={styles.timedOverlayProgressBar}>
                <div
                  className={styles.timedOverlayProgressFill}
                  style={{
                    animationDuration: `${currentTimedOverlay.durationMs}ms`,
                    ['--cn-timed-ms' as string]: `${currentTimedOverlay.durationMs}ms`,
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {showReviewWidget && game.currentScene && (
          <>
            <ReviewFab onClick={() => setIsReviewModalOpen(true)} />
            <ReviewCommentModal
              isOpen={isReviewModalOpen}
              onClose={() => setIsReviewModalOpen(false)}
              onSubmit={handleReviewSubmit}
              context={{
                scenarioTitle: scenario.metadata.title,
                scenarioId,
                sceneId: game.currentScene.id,
                missionLabel: missionLabel ?? undefined,
                sceneExcerpt: getSceneExcerptWithSpeaker(game.currentScene),
              }}
            />
          </>
        )}
      </div>
    </div>
  );
}

